import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import {
  CalendarDays,
  Check,
  CheckSquare,
  SquareX,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Copy,
  Link2,
  ListChevronsUpDown,
  Minus,
  MoreHorizontal,
  MousePointerClick,
  Lock,
  Pencil,
  Plus,
  Trash2,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EditorUndoControls } from "@/components/planHistory/EditorUndoControls";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import AiWorkoutBar from "@/components/workouts/ai/AiWorkoutBar";
import { useOrgFeatureFlags } from "@/lib/queries/useOrgFeatureFlags";
import EditWorkoutView from "@/components/workouts/editWorkoutView";
import ExerciseSelectList from "@/components/workouts/ExerciseSelectList";
import WorkoutEditorWorkspace from "@/components/workouts/WorkoutEditorWorkspace";
import { createWorkoutExercise } from "@/components/workouts/workoutEditorUtils";
import ImportWorkoutDialog from "@/components/workouts/importWorkoutDialog";
import StartWorkoutDialog from "@/components/workouts/StartWorkoutDialog";
import {
  apiWorkoutToEditor,
  ensureWorkoutSections,
  groupsForSection,
  isLooseSection,
} from "@/lib/utils/workoutSections";
import { sessionSectionLabel } from "@/lib/utils/workoutSectionClock";
import { Workout, Exercise, ExerciseSet } from "@/components/workouts/workoutsColumns";
import { Tables } from "@/lib/supabase/supabaseTypes";
import { Exercise as LibraryExercise } from "@/components/exercises/exerciseColumns";
import {
  AutoPeriodiseExerciseUpdate,
  PeriodisationMatrix,
  PeriodisationScheduleItem,
} from "@/components/training/PeriodisationMatrix";
import { cn } from "@/lib/utils/ui";
import { invokeEdgeFunction } from "@/lib/supabase/edgeFunctions";
import { useOrg } from "@/lib/states/session/hooks";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import {
  dateFromFixedDayIndex,
  endOfWeek as endOfDisplayWeek,
  fixedDayIndexFromDate,
  fixedVisualDays,
  isFixedDayIndexInView,
  startOfWeek as startOfDisplayWeek,
  visualWeekdayIndex,
  weekdayLabels,
} from "@/lib/dates/weekStart";
import { useVersionHistoryPreview } from "@/components/planHistory/VersionHistoryHost";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  TouchSensor,
  useDndContext,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFeatureFlagEnabled } from "@/components/PostHog/context";
import { CalendarBulkActionBar } from "@/components/training/CalendarBulkActionBar";
import {
  copyPlacementsPreservingSpacing,
  relocateScheduleGroup,
} from "@/components/training/calendarSelection";

type ScheduleContext = "client-fixed" | "client-dated" | "template-relative";
type SelectAllByNameSuggestion = {
  key: string;
  displayName: string;
  count: number;
};

export type CalendarScheduleItem = {
  id: string;
  workout_id: string;
  scheduled_date: string;
  order_index: number;
};

export type FixedScheduleItem = {
  id: string;
  workout_id: string;
  day_index: number;
  order_index: number;
  rolling_period?: number;
};

export type RelativeScheduleItem = {
  id: string;
  workout_id: string;
  day_offset: number;
  order_index: number;
};

type ClientTrainingCalendarViewProps = {
  clientId?: string;
  planSchedulingMode: "fixed" | "dated";
  scheduleContext?: ScheduleContext;
  workouts: Workout[];
  scheduleItems: CalendarScheduleItem[];
  fixedScheduleItems: FixedScheduleItem[];
  relativeScheduleItems?: RelativeScheduleItem[];
  fixedScheduleViewWeeks: number;
  relativePageSize?: number;
  relativePageStartOffset?: number;
  hasChanges: boolean;
  setHasChanges: (value: boolean) => void;
  onScheduleItemsChange: (items: CalendarScheduleItem[]) => void;
  onFixedScheduleItemsChange: (items: FixedScheduleItem[]) => void;
  onRelativeScheduleItemsChange?: (items: RelativeScheduleItem[]) => void;
  onCreateWorkoutForDate: (
    scheduledDate: string,
    workoutName: string,
  ) => string | undefined;
  onImportWorkoutForDate: (scheduledDate: string, workout: Workout) => void;
  onCopyWorkoutForDate: (scheduledDate: string, workout: Workout) => void;
  onCopyWorkoutsForDate?: (
    entries: { scheduledDate: string; workout: Workout }[],
  ) => void;
  onCreateWorkoutForDay: (
    dayIndex: number,
    workoutName: string,
  ) => string | undefined;
  onImportWorkoutForDay: (dayIndex: number, workout: Workout) => void;
  onCopyWorkoutForDay: (dayIndex: number, workout: Workout) => void;
  onCopyWorkoutsForDay?: (
    entries: { dayIndex: number; workout: Workout }[],
  ) => void;
  onAddExistingWorkoutToDay: (dayIndex: number, workout: Workout) => void;
  onFixedScheduleViewWeeksChange: (weeks: number) => void;
  onRelativePageStartOffsetChange?: (offset: number) => void;
  onSave: () => void;
  saveDisabled?: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => boolean | void;
  onRedo?: () => boolean | void;
  undoDisabled?: boolean;
  onDeletePlan?: () => void;
  isTemplateLinked?: boolean;
  onOpenLinkedTemplate?: () => void;
  onWorkoutUpdated: (workout: Workout) => void;
  commitWorkout?: (
    workoutId: string,
    producer: (workout: Workout) => Workout,
  ) => void;
  onDeleteWorkout: (workout: Workout) => void;
  onDeleteWorkouts?: (workouts: Workout[]) => void;
  onRemoveExerciseFromWorkout: (workoutId: string, exercise: Exercise) => void;
};

type WorkoutCopyClipboard = {
  sourceItemIds: string[];
  items: {
    workout: Workout;
    scheduledDate: string;
    orderIndex: number;
  }[];
};

const RELATIVE_BASE_DATE = startOfDay(new Date(2000, 0, 3));
const PERIODISATION_SWEEP_DURATION_MS = 4200;

type EdgeTrackingType =
  | "strength"
  | "bodyweight"
  | "timed"
  | "distance_short"
  | "distance_long";
type EdgeSet = {
  id: string;
  startRange?: number;
  endRange?: number;
  targetWeight?: number;
  targetTimeSeconds?: number;
  targetDistanceMeters?: number;
};
type EdgeRequest = {
  orgId: string;
  selectedItems: PeriodisationScheduleItem[];
  workouts: Array<{
    id: string;
    name: string;
    exercises: Array<{
      id: string;
      name: string;
      tracking_type: EdgeTrackingType;
      occurrence: number;
      sets: EdgeSet[];
    }>;
  }>;
};
type EdgeResponse = {
  updates: AutoPeriodiseExerciseUpdate[];
  skipped: Array<{
    workoutId: string;
    exerciseId: string;
    occurrence: number;
    reasonCode: string;
    reasonText: string;
  }>;
  summary: string;
  meta: {
    planner: "llm" | "fallback";
    model: string;
    version: "v1";
  };
};

const toDateKey = (date: Date) => format(date, "yyyy-MM-dd");

const normalizeScheduleForDate = (
  items: CalendarScheduleItem[],
  scheduledDate: string,
) => {
  const dateItems = items
    .filter((item) => item.scheduled_date === scheduledDate)
    .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
    .map((item, index) => ({ ...item, order_index: index }));

  const other = items.filter((item) => item.scheduled_date !== scheduledDate);
  return [...other, ...dateItems];
};

export function ClientTrainingCalendarView({
  clientId,
  planSchedulingMode,
  scheduleContext,
  workouts,
  scheduleItems,
  fixedScheduleItems,
  relativeScheduleItems = [],
  fixedScheduleViewWeeks,
  relativePageSize = 30,
  relativePageStartOffset,
  hasChanges,
  setHasChanges,
  onScheduleItemsChange,
  onFixedScheduleItemsChange,
  onRelativeScheduleItemsChange,
  onCreateWorkoutForDate,
  onImportWorkoutForDate,
  onCopyWorkoutForDate,
  onCopyWorkoutsForDate,
  onCreateWorkoutForDay,
  onImportWorkoutForDay,
  onCopyWorkoutForDay,
  onCopyWorkoutsForDay,
  onAddExistingWorkoutToDay,
  onFixedScheduleViewWeeksChange,
  onRelativePageStartOffsetChange,
  onSave,
  saveDisabled = false,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  undoDisabled = false,
  onDeletePlan,
  isTemplateLinked = false,
  onOpenLinkedTemplate,
  onWorkoutUpdated,
  commitWorkout,
  onDeleteWorkout,
  onDeleteWorkouts,
  onRemoveExerciseFromWorkout,
}: ClientTrainingCalendarViewProps) {
  const isAiAutoPeriodiseEnabled = useFeatureFlagEnabled("ai-auto-periodise");
  const { aiWorkoutBuilder } = useOrgFeatureFlags();
  const org = useOrg();
  const weekStartsOn = useWeekStartsOn();
  const weekdayHeaderLabels = useMemo(
    () => weekdayLabels(weekStartsOn),
    [weekStartsOn],
  );
  const resolvedScheduleContext: ScheduleContext =
    scheduleContext ??
    (planSchedulingMode === "fixed" ? "client-fixed" : "client-dated");
  const isFixedMode = resolvedScheduleContext === "client-fixed";
  const isClientDatedMode = resolvedScheduleContext === "client-dated";
  const isTemplateRelativeMode =
    resolvedScheduleContext === "template-relative";
  const isPreviewing = useVersionHistoryPreview();
  const isReadOnlyLinkedPlan = isTemplateLinked || isPreviewing;
  const [anchorDate, setAnchorDate] = useState<Date>(startOfDay(new Date()));
  const [selectedDateForCreate, setSelectedDateForCreate] = useState<
    string | null
  >(null);
  const [selectedFixedDayForSchedule, setSelectedFixedDayForSchedule] =
    useState<number | null>(null);
  const [relativePageStartOffsetState, setRelativePageStartOffsetState] =
    useState(relativePageStartOffset ?? 0);
  const [newWorkoutName, setNewWorkoutName] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [editingScheduleItemId, setEditingScheduleItemId] = useState<
    string | null
  >(null);
  const [activeScheduleItemId, setActiveScheduleItemId] = useState<
    string | null
  >(null);
  const [copyClipboard, setCopyClipboard] = useState<WorkoutCopyClipboard | null>(
    null,
  );
  const [periodisationSelectMode, setPeriodisationSelectMode] = useState(false);
  const [cardsExpanded, setCardsExpandedState] = useState(readCardsExpanded);
  const [collapsedSectionIdsByItem, setCollapsedSectionIdsByItem] = useState<
    Record<string, readonly string[]>
  >({});
  const cardSectionsByWorkoutId = useMemo(() => {
    const sectionsByWorkoutId = new Map<string, CalendarCardSection[]>();
    for (const workout of workouts) {
      sectionsByWorkoutId.set(workout.id, calendarCardSections(workout));
    }
    return sectionsByWorkoutId;
  }, [workouts]);
  const toggleCardSection = useCallback((itemId: string, sectionId: string) => {
    setCollapsedSectionIdsByItem((current) => {
      const existing = current[itemId] ?? [];
      const next = existing.includes(sectionId)
        ? existing.filter((id) => id !== sectionId)
        : [...existing, sectionId];
      return { ...current, [itemId]: next };
    });
  }, []);
  const setCardsExpanded = (expanded: boolean) => {
    setCardsExpandedState(expanded);
    try {
      window.localStorage.setItem(
        CALENDAR_CARD_DENSITY_KEY,
        expanded ? "expanded" : "collapsed",
      );
    } catch {
      // Ignore storage failures (private mode).
    }
  };
  const [selectedPeriodisationItemIds, setSelectedPeriodisationItemIds] =
    useState<string[]>([]);
  const [periodisationBoxSelectRect, setPeriodisationBoxSelectRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [isPeriodisationBoxSelecting, setIsPeriodisationBoxSelecting] =
    useState(false);
  const [
    hasPeriodisationBoxSelectionStarted,
    setHasPeriodisationBoxSelectionStarted,
  ] = useState(false);
  const [showSelectAllByNameSuggestion, setShowSelectAllByNameSuggestion] =
    useState(false);
  const [renderSelectAllByNameSuggestion, setRenderSelectAllByNameSuggestion] =
    useState<SelectAllByNameSuggestion | null>(null);
  const [activeSelectAllSuggestionKey, setActiveSelectAllSuggestionKey] =
    useState<string | null>(null);
  const [
    dismissedSelectAllSuggestionKeys,
    setDismissedSelectAllSuggestionKeys,
  ] = useState<string[]>([]);
  const [periodisationDialogOpen, setPeriodisationDialogOpen] = useState(false);
  const [periodisationSweepActive, setPeriodisationSweepActive] = useState(false);
  const [periodisationSweepMotion, setPeriodisationSweepMotion] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const calendarGridRef = useRef<HTMLDivElement | null>(null);
  const weekdayHeaderScrollRef = useRef<HTMLDivElement | null>(null);
  const calendarBodyScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingCalendarScrollRef = useRef(false);
  const boxSelectPointerIdRef = useRef<number | null>(null);
  const boxSelectOriginGridRef = useRef<{ x: number; y: number } | null>(null);
  const boxSelectLastClientRef = useRef<{ x: number; y: number } | null>(null);
  const boxSelectStartClientRef = useRef<{ x: number; y: number } | null>(null);
  const boxSelectScrollContainerRef = useRef<HTMLElement | null>(null);
  const boxSelectAutoScrollRafRef = useRef<number | null>(null);
  const selectAllByNameSuggestionTimeoutRef = useRef<number | null>(null);
  const selectAllByNameSuggestionHideTimeoutRef = useRef<number | null>(null);
  const suppressDayClickRef = useRef(false);
  const boxSelectCleanupRef = useRef<(() => void) | null>(null);
  const selectionAnchorIdRef = useRef<string | null>(null);
  const orderedScheduleItemIdsRef = useRef<string[]>([]);
  const boxSelectBaseIdsRef = useRef<string[]>([]);
  const editingWorkoutBottomRef = useRef<HTMLDivElement | null>(null);
  const [badgeCenterX, setBadgeCenterX] = useState<number | null>(null);

  const workoutById = useMemo(
    () => new Map(workouts.map((workout) => [workout.id, workout])),
    [workouts],
  );
  const effectiveRelativePageStartOffset =
    relativePageStartOffset ?? relativePageStartOffsetState;
  const setEffectiveRelativePageStartOffset = useCallback(
    (nextOffset: number) => {
      const normalized = Math.max(0, nextOffset);
      if (relativePageStartOffset === undefined) {
        setRelativePageStartOffsetState(normalized);
      }
      onRelativePageStartOffsetChange?.(normalized);
    },
    [onRelativePageStartOffsetChange, relativePageStartOffset],
  );

  const fixedWeekStart = useMemo(
    () => startOfDisplayWeek(anchorDate, 1),
    [anchorDate],
  );
  const fixedVisibleDays = useMemo(
    () => fixedScheduleViewWeeks * 7,
    [fixedScheduleViewWeeks],
  );
  const fixedDateFromDayIndex = useCallback(
    (dayIndex: number) =>
      toDateKey(dateFromFixedDayIndex(fixedWeekStart, dayIndex)),
    [fixedWeekStart],
  );
  const fixedDayIndexFromDateKey = useCallback(
    (dateKey: string) =>
      fixedDayIndexFromDate(fixedWeekStart, new Date(dateKey)),
    [fixedWeekStart],
  );
  const isFixedDateSchedulable = useCallback(
    (dateKey: string) =>
      isFixedDayIndexInView(
        fixedDayIndexFromDateKey(dateKey),
        fixedVisibleDays,
      ),
    [fixedDayIndexFromDateKey, fixedVisibleDays],
  );
  const relativeDateFromDayOffset = useCallback(
    (dayOffset: number) => toDateKey(addDays(RELATIVE_BASE_DATE, dayOffset)),
    [],
  );
  const relativeDayOffsetFromDateKey = useCallback(
    (dateKey: string) =>
      Math.max(
        0,
        differenceInCalendarDays(new Date(dateKey), RELATIVE_BASE_DATE),
      ),
    [],
  );
  const scheduleSlotFromDateKey = useCallback(
    (dateKey: string) => {
      if (isFixedMode) return fixedDayIndexFromDateKey(dateKey);
      if (isTemplateRelativeMode) return relativeDayOffsetFromDateKey(dateKey);
      return 0;
    },
    [
      fixedDayIndexFromDateKey,
      isFixedMode,
      isTemplateRelativeMode,
      relativeDayOffsetFromDateKey,
    ],
  );
  const normalizeRelativeScheduleForOffset = useCallback(
    (items: RelativeScheduleItem[], dayOffset: number) => {
      const dayItems = items
        .filter((item) => item.day_offset === dayOffset)
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
        .map((item, index) => ({ ...item, order_index: index }));
      const other = items.filter((item) => item.day_offset !== dayOffset);
      return [...other, ...dayItems];
    },
    [],
  );
  const commitDisplayScheduleItems = useCallback(
    (
      items: CalendarScheduleItem[],
      dayKeysToNormalize: string[],
      options?: { preservePeriodisationSelection?: boolean },
    ) => {
      if (isFixedMode) {
        onFixedScheduleItemsChange(
          items.map((item) => ({
            id: item.id,
            workout_id: item.workout_id,
            day_index: scheduleSlotFromDateKey(item.scheduled_date),
            order_index: item.order_index ?? 0,
            rolling_period: fixedVisibleDays,
          })),
        );
      } else if (isTemplateRelativeMode) {
        if (!onRelativeScheduleItemsChange) return;
        let mappedItems = items.map((item) => ({
          id: item.id,
          workout_id: item.workout_id,
          day_offset: relativeDayOffsetFromDateKey(item.scheduled_date),
          order_index: item.order_index ?? 0,
        }));
        dayKeysToNormalize.forEach((dayKey) => {
          mappedItems = normalizeRelativeScheduleForOffset(
            mappedItems,
            relativeDayOffsetFromDateKey(dayKey),
          );
        });
        onRelativeScheduleItemsChange(mappedItems);
      } else {
        let normalizedItems = [...items];
        dayKeysToNormalize.forEach((dayKey) => {
          normalizedItems = normalizeScheduleForDate(normalizedItems, dayKey);
        });
        onScheduleItemsChange(normalizedItems);
      }
      if (!options?.preservePeriodisationSelection) {
        setSelectedPeriodisationItemIds((prev) =>
          prev.filter((id) => items.some((item) => item.id === id)),
        );
      }
    },
    [
      fixedVisibleDays,
      isFixedMode,
      isTemplateRelativeMode,
      normalizeRelativeScheduleForOffset,
      onFixedScheduleItemsChange,
      onRelativeScheduleItemsChange,
      onScheduleItemsChange,
      relativeDayOffsetFromDateKey,
      scheduleSlotFromDateKey,
    ],
  );

  const displayScheduleItems = useMemo<CalendarScheduleItem[]>(
    () =>
      isFixedMode
        ? fixedScheduleItems.map((item) => ({
            id: item.id,
            workout_id: item.workout_id,
            order_index: item.order_index ?? 0,
            scheduled_date: fixedDateFromDayIndex(item.day_index ?? 0),
          }))
        : isTemplateRelativeMode
          ? relativeScheduleItems.map((item) => ({
              id: item.id,
              workout_id: item.workout_id,
              order_index: item.order_index ?? 0,
              scheduled_date: relativeDateFromDayOffset(item.day_offset ?? 0),
            }))
          : scheduleItems,
    [
      fixedDateFromDayIndex,
      fixedScheduleItems,
      isFixedMode,
      isTemplateRelativeMode,
      relativeDateFromDayOffset,
      relativeScheduleItems,
      scheduleItems,
    ],
  );

  const scheduleByDate = useMemo(() => {
    const grouped = new Map<string, CalendarScheduleItem[]>();
    displayScheduleItems.forEach((item) => {
      const list = grouped.get(item.scheduled_date) ?? [];
      list.push(item);
      grouped.set(item.scheduled_date, list);
    });
    grouped.forEach((list, key) => {
      grouped.set(
        key,
        [...list].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)),
      );
    });
    return grouped;
  }, [displayScheduleItems]);
  const orderedScheduleItemIds = useMemo(
    () =>
      [...displayScheduleItems]
        .sort((a, b) => {
          if (a.scheduled_date !== b.scheduled_date) {
            return a.scheduled_date.localeCompare(b.scheduled_date);
          }
          return (a.order_index ?? 0) - (b.order_index ?? 0);
        })
        .map((item) => item.id),
    [displayScheduleItems],
  );
  orderedScheduleItemIdsRef.current = orderedScheduleItemIds;

  const editingScheduleItem = useMemo(
    () =>
      displayScheduleItems.find((item) => item.id === editingScheduleItemId) ??
      null,
    [displayScheduleItems, editingScheduleItemId],
  );

  const editingWorkout = useMemo(
    () =>
      editingScheduleItem
        ? (workoutById.get(editingScheduleItem.workout_id) ?? null)
        : null,
    [editingScheduleItem, workoutById],
  );
  const copyingLabel = useMemo(() => {
    if (!copyClipboard || copyClipboard.items.length === 0) return null;
    if (copyClipboard.items.length === 1) {
      return copyClipboard.items[0]?.workout.name ?? "workout";
    }
    return `${copyClipboard.items.length} workouts`;
  }, [copyClipboard]);
  const editingScheduleDateLabel = useMemo(() => {
    if (!editingScheduleItem?.scheduled_date) return "";
    if (isTemplateRelativeMode) {
      return `Day ${relativeDayOffsetFromDateKey(editingScheduleItem.scheduled_date) + 1}`;
    }
    return format(
      new Date(`${editingScheduleItem.scheduled_date}T00:00:00`),
      "EEE, MMM d",
    );
  }, [
    editingScheduleItem?.scheduled_date,
    isTemplateRelativeMode,
    relativeDayOffsetFromDateKey,
  ]);
  const selectedPeriodisationItems = useMemo(
    () =>
      displayScheduleItems
        .filter((item) => selectedPeriodisationItemIds.includes(item.id))
        .sort((a, b) => {
          if (a.scheduled_date !== b.scheduled_date) {
            return a.scheduled_date.localeCompare(b.scheduled_date);
          }
          return (a.order_index ?? 0) - (b.order_index ?? 0);
        }),
    [displayScheduleItems, selectedPeriodisationItemIds],
  );
  const selectAllByNameSuggestion =
    useMemo<SelectAllByNameSuggestion | null>(() => {
      if (!periodisationSelectMode || selectedPeriodisationItems.length < 2) {
        return null;
      }

      const nameCounts = new Map<
        string,
        { displayName: string; count: number }
      >();
      selectedPeriodisationItems.forEach((item) => {
        const workout = workoutById.get(item.workout_id);
        if (!workout) return;
        const trimmedName = workout.name.trim();
        if (!trimmedName) return;
        const key = trimmedName.toLowerCase();
        const existing = nameCounts.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          nameCounts.set(key, { displayName: trimmedName, count: 1 });
        }
      });

      const best = Array.from(nameCounts.entries())
        .map(([key, value]) => ({
          key,
          displayName: value.displayName,
          count: value.count,
        }))
        .sort((a, b) => b.count - a.count)[0];

      if (!best || best.count < 2) return null;
      return best;
    }, [periodisationSelectMode, selectedPeriodisationItems, workoutById]);
  const selectAllByNameSuggestionKey = selectAllByNameSuggestion?.key ?? null;
  const resolvePeriodisationBoxSelectItemIds = useCallback(
    (selectionRect: {
      left: number;
      top: number;
      right: number;
      bottom: number;
    }) => {
      const node = calendarGridRef.current;
      if (!node) return [];
      const cards = node.querySelectorAll<HTMLElement>(
        "[data-periodisation-item-id]",
      );
      const selectedIds: string[] = [];
      cards.forEach((card) => {
        const itemId = card.dataset.periodisationItemId;
        if (!itemId) return;
        const rect = card.getBoundingClientRect();
        const intersects =
          rect.left < selectionRect.right &&
          rect.right > selectionRect.left &&
          rect.top < selectionRect.bottom &&
          rect.bottom > selectionRect.top;
        if (intersects) {
          selectedIds.push(itemId);
        }
      });
      return selectedIds;
    },
    [],
  );
  const calendarDays = useMemo(() => {
    if (isFixedMode) {
      return fixedVisualDays(
        fixedWeekStart,
        fixedVisibleDays,
        weekStartsOn,
      );
    }
    if (isTemplateRelativeMode) {
      return Array.from({ length: relativePageSize }, (_, idx) =>
        addDays(RELATIVE_BASE_DATE, effectiveRelativePageStartOffset + idx),
      );
    }

    const monthStart = startOfMonth(anchorDate);
    const monthEnd = endOfMonth(anchorDate);
    const gridStart = startOfDisplayWeek(monthStart, weekStartsOn);
    const gridEnd = endOfDisplayWeek(monthEnd, weekStartsOn);

    const days: Date[] = [];
    for (let date = gridStart; date <= gridEnd; date = addDays(date, 1)) {
      days.push(date);
    }
    return days;
  }, [
    anchorDate,
    effectiveRelativePageStartOffset,
    fixedVisibleDays,
    fixedWeekStart,
    isFixedMode,
    isTemplateRelativeMode,
    relativePageSize,
    weekStartsOn,
  ]);
  const weekdayItemIdsInView = useMemo(() => {
    const grouped = new Map<number, string[]>();
    calendarDays.forEach((day) => {
      const weekdayIndex = visualWeekdayIndex(day, weekStartsOn);
      const dateKey = toDateKey(day);
      const dayItems = scheduleByDate.get(dateKey) ?? [];
      const existing = grouped.get(weekdayIndex) ?? [];
      grouped.set(weekdayIndex, [
        ...existing,
        ...dayItems.map((item) => item.id),
      ]);
    });
    return grouped;
  }, [
    anchorDate,
    calendarDays,
    isFixedMode,
    isTemplateRelativeMode,
    scheduleByDate,
    weekStartsOn,
  ]);
  const weekdaySelectionState = useMemo(() => {
    const selected = new Set(selectedPeriodisationItemIds);
    return weekdayHeaderLabels.map((_, weekdayIndex) => {
      const ids = weekdayItemIdsInView.get(weekdayIndex) ?? [];
      const selectedCount = ids.filter((id) => selected.has(id)).length;
      return {
        allSelected: ids.length > 0 && selectedCount === ids.length,
        partiallySelected: selectedCount > 0 && selectedCount < ids.length,
      };
    });
  }, [selectedPeriodisationItemIds, weekdayHeaderLabels, weekdayItemIdsInView]);

  const navigatePrevious = () => {
    if (isTemplateRelativeMode) {
      setEffectiveRelativePageStartOffset(
        effectiveRelativePageStartOffset - relativePageSize,
      );
      return;
    }
    setAnchorDate((prev) => subMonths(prev, 1));
  };

  const navigateNext = () => {
    if (isTemplateRelativeMode) {
      setEffectiveRelativePageStartOffset(
        effectiveRelativePageStartOffset + relativePageSize,
      );
      return;
    }
    setAnchorDate((prev) => addMonths(prev, 1));
  };

  const goToToday = () => {
    setAnchorDate(startOfDay(new Date()));
    window.setTimeout(() => {
      const todayKey = toDateKey(startOfDay(new Date()));
      const node = document.querySelector<HTMLElement>(
        `[data-calendar-day-key="${todayKey}"]`,
      );
      node?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
  };

  const openCreateDialog = (scheduledDate: string) => {
    setSelectedDateForCreate(scheduledDate);
    setNewWorkoutName("");
  };

  const removeScheduleItems = useCallback(
    (itemIds: string[]) => {
      if (isReadOnlyLinkedPlan || itemIds.length === 0) return;
      const idSet = new Set(itemIds);
      const items = displayScheduleItems.filter((entry) => idSet.has(entry.id));
      if (items.length === 0) return;

      if (isTemplateRelativeMode) {
        const workoutsToDelete = items
          .map((item) => workoutById.get(item.workout_id))
          .filter((workout): workout is Workout => Boolean(workout));
        if (onDeleteWorkouts) {
          onDeleteWorkouts(workoutsToDelete);
        } else {
          workoutsToDelete.forEach((workout) => onDeleteWorkout(workout));
        }
      } else {
        const next = displayScheduleItems.filter((entry) => !idSet.has(entry.id));
        const dates = Array.from(
          new Set(items.map((item) => item.scheduled_date)),
        );
        commitDisplayScheduleItems(next, dates);
      }

      setSelectedPeriodisationItemIds((prev) =>
        prev.filter((id) => !idSet.has(id)),
      );
      setCopyClipboard((prev) => {
        if (!prev) return prev;
        const sourceItemIds = prev.sourceItemIds.filter((id) => !idSet.has(id));
        if (sourceItemIds.length === 0) return null;
        return { ...prev, sourceItemIds };
      });
      if (editingScheduleItemId && idSet.has(editingScheduleItemId)) {
        setEditingScheduleItemId(null);
      }
    },
    [
      commitDisplayScheduleItems,
      displayScheduleItems,
      editingScheduleItemId,
      isReadOnlyLinkedPlan,
      isTemplateRelativeMode,
      onDeleteWorkout,
      onDeleteWorkouts,
      workoutById,
    ],
  );

  const removeScheduleItem = (itemId: string) => {
    removeScheduleItems([itemId]);
  };

  const beginCopy = useCallback(
    (items: CalendarScheduleItem[]) => {
      if (isReadOnlyLinkedPlan || isFixedMode || items.length === 0) return;
      const clipboardItems = items.flatMap((item) => {
        const workout = workoutById.get(item.workout_id);
        if (!workout) return [];
        return [
          {
            workout,
            scheduledDate: item.scheduled_date,
            orderIndex: item.order_index ?? 0,
          },
        ];
      });
      if (clipboardItems.length === 0) return;
      setCopyClipboard({
        sourceItemIds: items.map((item) => item.id),
        items: clipboardItems,
      });
      setSelectedPeriodisationItemIds([]);
      selectionAnchorIdRef.current = null;
      setPeriodisationSelectMode(false);
      setPeriodisationBoxSelectRect(null);
    },
    [isFixedMode, isReadOnlyLinkedPlan, workoutById],
  );

  const pasteCopyAtDate = useCallback(
    (scheduledDate: string) => {
      if (isReadOnlyLinkedPlan || !copyClipboard) return;
      if (isFixedMode && !isFixedDateSchedulable(scheduledDate)) return;
      const placed = copyPlacementsPreservingSpacing(
        copyClipboard.items,
        scheduledDate,
      );
      if (placed.length === 0) return;

      if (isFixedMode || isTemplateRelativeMode) {
        const entries = placed.map((item) => ({
          dayIndex: Math.max(0, scheduleSlotFromDateKey(item.scheduledDate)),
          workout: item.workout,
        }));
        if (onCopyWorkoutsForDay) {
          onCopyWorkoutsForDay(entries);
        } else {
          entries.forEach((entry) =>
            onCopyWorkoutForDay(entry.dayIndex, entry.workout),
          );
        }
        return;
      }

      const entries = placed.map((item) => ({
        scheduledDate: item.scheduledDate,
        workout: item.workout,
      }));
      if (onCopyWorkoutsForDate) {
        onCopyWorkoutsForDate(entries);
      } else {
        entries.forEach((entry) =>
          onCopyWorkoutForDate(entry.scheduledDate, entry.workout),
        );
      }
    },
    [
      copyClipboard,
      isFixedDateSchedulable,
      isFixedMode,
      isReadOnlyLinkedPlan,
      isTemplateRelativeMode,
      onCopyWorkoutForDate,
      onCopyWorkoutForDay,
      onCopyWorkoutsForDate,
      onCopyWorkoutsForDay,
      scheduleSlotFromDateKey,
    ],
  );

  const consumeSelectionClick = () => {
    if (!suppressDayClickRef.current) return false;
    suppressDayClickRef.current = false;
    return true;
  };

  const handleDayCellClick = (
    scheduledDate: string,
    options?: { create?: boolean },
  ) => {
    if (consumeSelectionClick()) return;
    if (isReadOnlyLinkedPlan) return;
    if (periodisationSelectMode) return;
    if (copyClipboard) {
      pasteCopyAtDate(scheduledDate);
      return;
    }
    if (!options?.create && selectedPeriodisationItemIds.length > 0) {
      setSelectedPeriodisationItemIds([]);
      selectionAnchorIdRef.current = null;
      setPeriodisationBoxSelectRect(null);
      return;
    }
    if (isFixedMode) {
      if (!isFixedDateSchedulable(scheduledDate)) return;
      setSelectedFixedDayForSchedule(scheduleSlotFromDateKey(scheduledDate));
      return;
    }
    openCreateDialog(scheduledDate);
  };

  const togglePeriodisationSelectMode = () => {
    if (isReadOnlyLinkedPlan) return;
    if (periodisationSelectMode) {
      setPeriodisationSelectMode(false);
      setSelectedPeriodisationItemIds([]);
      selectionAnchorIdRef.current = null;
      setPeriodisationBoxSelectRect(null);
      return;
    }
    setPeriodisationSelectMode(true);
    setSelectedPeriodisationItemIds([]);
    selectionAnchorIdRef.current = null;
    setPeriodisationBoxSelectRect(null);
  };

  const togglePeriodisationSelection = (itemId: string) => {
    selectionAnchorIdRef.current = itemId;
    setSelectedPeriodisationItemIds((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };
  const addScheduleRangeToSelection = (itemId: string) => {
    const ordered = orderedScheduleItemIdsRef.current;
    const targetIndex = ordered.indexOf(itemId);
    if (targetIndex < 0) return;
    const anchorId = selectionAnchorIdRef.current;
    const anchorIndex = anchorId ? ordered.indexOf(anchorId) : -1;
    if (anchorIndex < 0) {
      selectionAnchorIdRef.current = itemId;
      setSelectedPeriodisationItemIds((prev) =>
        prev.includes(itemId) ? prev : [...prev, itemId],
      );
      return;
    }
    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    const rangeIds = ordered.slice(start, end + 1);
    setSelectedPeriodisationItemIds((prev) => {
      const merged = new Set(prev);
      rangeIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  };
  const toggleWeekdayPeriodisationSelection = (weekdayIndex: number) => {
    const itemIds = weekdayItemIdsInView.get(weekdayIndex) ?? [];
    if (itemIds.length === 0) return;
    setSelectedPeriodisationItemIds((prev) => {
      const itemIdSet = new Set(itemIds);
      const allSelected = itemIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !itemIdSet.has(id));
      }
      const merged = new Set(prev);
      itemIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  };
  const selectAllUpcomingWorkoutsByName = useCallback(() => {
    if (!selectAllByNameSuggestion) return;
    const todayKey = toDateKey(startOfDay(new Date()));
    const matchingIds = displayScheduleItems
      .filter((item) => {
        const workout = workoutById.get(item.workout_id);
        if (!workout) return false;
        const matchesName =
          workout.name.trim().toLowerCase() === selectAllByNameSuggestion.key;
        if (!matchesName) return false;
        if (isClientDatedMode) {
          return item.scheduled_date >= todayKey;
        }
        return true;
      })
      .map((item) => item.id);
    if (matchingIds.length === 0) return;
    setSelectedPeriodisationItemIds((prev) => {
      const merged = new Set(prev);
      matchingIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
    setDismissedSelectAllSuggestionKeys((prev) =>
      prev.includes(selectAllByNameSuggestion.key)
        ? prev
        : [...prev, selectAllByNameSuggestion.key],
    );
    setShowSelectAllByNameSuggestion(false);
  }, [
    displayScheduleItems,
    isClientDatedMode,
    selectAllByNameSuggestion,
    workoutById,
  ]);
  const getClosestVerticalScrollContainer = useCallback(
    (node: HTMLElement | null) => {
      let current = node?.parentElement ?? null;
      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        const canScrollY =
          (overflowY === "auto" ||
            overflowY === "scroll" ||
            overflowY === "overlay") &&
          current.scrollHeight > current.clientHeight + 1;
        if (canScrollY) return current;
        current = current.parentElement;
      }
      return null;
    },
    [],
  );
  const updatePeriodisationBoxSelection = useCallback(
    (clientX: number, clientY: number) => {
      const origin = boxSelectOriginGridRef.current;
      const gridNode = calendarGridRef.current;
      if (!origin || !gridNode) return;

      const gridRect = gridNode.getBoundingClientRect();
      const localX = clientX - gridRect.left;
      const localY = clientY - gridRect.top;
      const clampedX = Math.min(Math.max(localX, 0), gridRect.width);
      const clampedY = Math.min(Math.max(localY, 0), gridRect.height);

      const left = Math.min(origin.x, clampedX);
      const right = Math.max(origin.x, clampedX);
      const top = Math.min(origin.y, clampedY);
      const bottom = Math.max(origin.y, clampedY);

      setPeriodisationBoxSelectRect({
        left,
        top,
        width: Math.max(0, right - left),
        height: Math.max(0, bottom - top),
      });
      const selectedIds = resolvePeriodisationBoxSelectItemIds({
        left: gridRect.left + left,
        top: gridRect.top + top,
        right: gridRect.left + right,
        bottom: gridRect.top + bottom,
      });
      const mergedIds =
        boxSelectBaseIdsRef.current.length > 0
          ? Array.from(new Set([...boxSelectBaseIdsRef.current, ...selectedIds]))
          : selectedIds;
      setSelectedPeriodisationItemIds(mergedIds);
      const ordered = orderedScheduleItemIdsRef.current;
      let anchor: string | null = null;
      for (let index = ordered.length - 1; index >= 0; index -= 1) {
        if (mergedIds.includes(ordered[index])) {
          anchor = ordered[index];
          break;
        }
      }
      selectionAnchorIdRef.current = anchor;
    },
    [resolvePeriodisationBoxSelectItemIds],
  );
  const handleCalendarGridPointerDown = (
    event: ReactPointerEvent<HTMLDivElement>,
  ) => {
    if (isReadOnlyLinkedPlan || copyClipboard) return;
    if (event.button !== 0) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.closest("[data-periodisation-item-id]")) return;
    if (target.closest("[data-periodisation-weekday-header]")) return;
    if (
      target.closest("button, a, input, textarea, select, [role='menuitem']")
    ) {
      return;
    }
    const gridNode = calendarGridRef.current;
    if (!gridNode) return;

    const pointerId = event.pointerId;
    const startX = event.clientX;
    const startY = event.clientY;
    const rect = gridNode.getBoundingClientRect();
    boxSelectPointerIdRef.current = pointerId;
    boxSelectOriginGridRef.current = {
      x: Math.min(Math.max(startX, rect.left), rect.right) - rect.left,
      y: Math.min(Math.max(startY, rect.top), rect.bottom) - rect.top,
    };
    boxSelectStartClientRef.current = { x: startX, y: startY };
    boxSelectLastClientRef.current = { x: startX, y: startY };
    boxSelectScrollContainerRef.current =
      getClosestVerticalScrollContainer(gridNode);
    boxSelectBaseIdsRef.current = event.shiftKey
      ? [...selectedPeriodisationItemIds]
      : [];

    let started = false;
    const listeners: {
      move: (ev: PointerEvent) => void;
      up: (ev: PointerEvent) => void;
    } = {
      move: () => {},
      up: () => {},
    };

    const cleanup = () => {
      window.removeEventListener("pointermove", listeners.move);
      window.removeEventListener("pointerup", listeners.up);
      window.removeEventListener("pointercancel", listeners.up);
      if (boxSelectCleanupRef.current === cleanup) {
        boxSelectCleanupRef.current = null;
      }
    };

    listeners.move = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      boxSelectLastClientRef.current = { x: ev.clientX, y: ev.clientY };
      if (!started) {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (Math.hypot(dx, dy) < 6) return;
        started = true;
        setIsPeriodisationBoxSelecting(true);
        setHasPeriodisationBoxSelectionStarted(true);
        if (boxSelectBaseIdsRef.current.length === 0) {
          setSelectedPeriodisationItemIds([]);
        }
      }
      updatePeriodisationBoxSelection(ev.clientX, ev.clientY);
      ev.preventDefault();
    };

    listeners.up = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      cleanup();
      if (started) {
        suppressDayClickRef.current = true;
        window.setTimeout(() => {
          suppressDayClickRef.current = false;
        }, 0);
      }
      boxSelectPointerIdRef.current = null;
      boxSelectOriginGridRef.current = null;
      boxSelectStartClientRef.current = null;
      boxSelectLastClientRef.current = null;
      boxSelectScrollContainerRef.current = null;
      setIsPeriodisationBoxSelecting(false);
      setHasPeriodisationBoxSelectionStarted(false);
      setPeriodisationBoxSelectRect(null);
    };

    boxSelectCleanupRef.current?.();
    boxSelectCleanupRef.current = cleanup;
    window.addEventListener("pointermove", listeners.move);
    window.addEventListener("pointerup", listeners.up);
    window.addEventListener("pointercancel", listeners.up);
  };

  const updateWorkoutExerciseFromPeriodisation = (
    workoutId: string,
    exerciseId: string,
    updates: {
      sets?: Exercise["sets"];
    },
    occurrence?: number,
  ) => {
    const workout = workoutById.get(workoutId);
    if (!workout) return;

    let changed = false;
    let seenOccurrence = -1;
    const nextCollection = workout.exerciseCollection.map((group) =>
      group.map((exercise) => {
        if (exercise.id !== exerciseId) return exercise;
        seenOccurrence += 1;
        if (occurrence !== undefined && seenOccurrence !== occurrence) return exercise;

        const rebuiltSets = updates.sets ?? exercise.sets;
        changed = true;
        return { ...exercise, sets: rebuiltSets };
      }),
    );

    if (!changed) return;
    onWorkoutUpdated({
      ...workout,
      exerciseCollection: nextCollection,
    });
    setHasChanges(true);
  };

  const updateWorkoutExercisesFromPeriodisationBatch = (
    updates: AutoPeriodiseExerciseUpdate[],
  ) => {
    if (updates.length === 0) return;

    const updatesByWorkout = new Map<string, AutoPeriodiseExerciseUpdate[]>();
    updates.forEach((update) => {
      const existing = updatesByWorkout.get(update.workoutId);
      if (existing) {
        existing.push(update);
      } else {
        updatesByWorkout.set(update.workoutId, [update]);
      }
    });

    let anyChanged = false;
    updatesByWorkout.forEach((workoutUpdates, workoutId) => {
      const workout = workoutById.get(workoutId);
      if (!workout) return;

      const updateByKey = new Map<string, ExerciseSet[]>();
      workoutUpdates.forEach((update) => {
        updateByKey.set(`${update.exerciseId}:${update.occurrence}`, update.sets);
      });

      if (updateByKey.size === 0) return;

      const occurrenceByExerciseId = new Map<string, number>();
      let workoutChanged = false;

      const nextCollection = workout.exerciseCollection.map((group) =>
        group.map((exercise) => {
          const occurrence = occurrenceByExerciseId.get(exercise.id) ?? 0;
          occurrenceByExerciseId.set(exercise.id, occurrence + 1);
          const nextSets = updateByKey.get(`${exercise.id}:${occurrence}`);
          if (!nextSets) return exercise;
          workoutChanged = true;
          return { ...exercise, sets: nextSets };
        }),
      );

      if (!workoutChanged) return;
      anyChanged = true;
      onWorkoutUpdated({
        ...workout,
        exerciseCollection: nextCollection,
      });
    });

    if (anyChanged) {
      setHasChanges(true);
    }
  };

  const generateAutoPeriodisationFromEdge = useCallback(async () => {
    if (!org?.id) {
      toast.error("No active organisation selected");
      throw new Error("No active organisation selected");
    }

    const selectedItems = selectedPeriodisationItems as PeriodisationScheduleItem[];
    const workoutIds = Array.from(new Set(selectedItems.map((item) => item.workout_id)));
    const workoutsPayload = workoutIds
      .map((workoutId) => {
        const workout = workoutById.get(workoutId);
        if (!workout) return null;
        const occurrenceByExerciseId = new Map<string, number>();
        const exercises = workout.exerciseCollection.flat().map((exercise) => {
          const occurrence = occurrenceByExerciseId.get(exercise.id) ?? 0;
          occurrenceByExerciseId.set(exercise.id, occurrence + 1);
          return {
            id: exercise.id,
            name: exercise.name,
            tracking_type: (exercise.tracking_type ?? "strength") as EdgeTrackingType,
            occurrence,
            sets: (exercise.sets ?? []).map((set) => ({
              id: set.id,
              startRange: set.startRange,
              endRange: set.endRange,
              targetWeight: set.targetWeight,
              targetTimeSeconds: set.targetTimeSeconds,
              targetDistanceMeters: set.targetDistanceMeters,
            })),
          };
        });
        return {
          id: workout.id,
          name: workout.name,
          exercises,
        };
      })
      .filter((workout): workout is NonNullable<typeof workout> => Boolean(workout));

    const payload: EdgeRequest = {
      orgId: org.id,
      selectedItems,
      workouts: workoutsPayload,
    };

    const { data, error } = await invokeEdgeFunction<EdgeResponse>(
      "auto-periodise-workouts",
      { body: payload },
    );

    if (error) {
      console.error("Auto periodisation edge call failed", error);
      toast.error("Failed to generate progression");
      throw new Error(error.message ?? "Failed to generate progression");
    }

    if (!data) {
      toast.error("No progression data returned");
      throw new Error("No progression data returned");
    }

    console.log("[auto-periodise-workouts] full response", data);

    if (data.summary) {
      toast.success(data.summary);
    }

    if (data.skipped.length > 0) {
      toast(`${data.skipped.length} items were skipped`);
    }

    return data.updates ?? [];
  }, [org?.id, selectedPeriodisationItems, workoutById]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateBadgeCenter = () => {
      const rect = node.getBoundingClientRect();
      setBadgeCenterX(rect.left + rect.width / 2);
    };

    updateBadgeCenter();
    const observer = new ResizeObserver(updateBadgeCenter);
    observer.observe(node);
    window.addEventListener("resize", updateBadgeCenter);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateBadgeCenter);
    };
  }, []);

  useEffect(() => () => boxSelectCleanupRef.current?.(), []);

  useEffect(() => {
    if (!copyClipboard) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Enter") {
        event.preventDefault();
        setCopyClipboard(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [copyClipboard]);

  useEffect(() => {
    if (!isReadOnlyLinkedPlan) return;
    setActiveScheduleItemId(null);
    setCopyClipboard(null);
    setPeriodisationSelectMode(false);
    setSelectedPeriodisationItemIds([]);
    selectionAnchorIdRef.current = null;
    setPeriodisationBoxSelectRect(null);
    setPeriodisationDialogOpen(false);
  }, [isReadOnlyLinkedPlan]);

  useEffect(() => {
    if (!periodisationSweepActive) {
      setPeriodisationSweepMotion(false);
      return;
    }
    setPeriodisationSweepMotion(false);
    const raf = window.requestAnimationFrame(() => {
      setPeriodisationSweepMotion(true);
    });
    return () => {
      window.cancelAnimationFrame(raf);
    };
  }, [periodisationSweepActive]);

  useEffect(() => {
    if (selectAllByNameSuggestionTimeoutRef.current !== null) {
      window.clearTimeout(selectAllByNameSuggestionTimeoutRef.current);
      selectAllByNameSuggestionTimeoutRef.current = null;
    }
    if (selectAllByNameSuggestionHideTimeoutRef.current !== null) {
      window.clearTimeout(selectAllByNameSuggestionHideTimeoutRef.current);
      selectAllByNameSuggestionHideTimeoutRef.current = null;
    }

    if (
      !periodisationSelectMode ||
      !selectAllByNameSuggestionKey ||
      dismissedSelectAllSuggestionKeys.includes(selectAllByNameSuggestionKey)
    ) {
      setShowSelectAllByNameSuggestion(false);
      if (renderSelectAllByNameSuggestion) {
        selectAllByNameSuggestionHideTimeoutRef.current = window.setTimeout(
          () => {
            setRenderSelectAllByNameSuggestion(null);
            setActiveSelectAllSuggestionKey(null);
            selectAllByNameSuggestionHideTimeoutRef.current = null;
          },
          220,
        );
      } else {
        setActiveSelectAllSuggestionKey(null);
      }
      return;
    }

    if (selectAllByNameSuggestion) {
      setRenderSelectAllByNameSuggestion(selectAllByNameSuggestion);
    }

    if (activeSelectAllSuggestionKey === selectAllByNameSuggestionKey) {
      setShowSelectAllByNameSuggestion(true);
      return;
    }

    setShowSelectAllByNameSuggestion(false);
    selectAllByNameSuggestionTimeoutRef.current = window.setTimeout(() => {
      setActiveSelectAllSuggestionKey(selectAllByNameSuggestionKey);
      setShowSelectAllByNameSuggestion(true);
      selectAllByNameSuggestionTimeoutRef.current = null;
    }, 400);

    return () => {
      if (selectAllByNameSuggestionTimeoutRef.current !== null) {
        window.clearTimeout(selectAllByNameSuggestionTimeoutRef.current);
        selectAllByNameSuggestionTimeoutRef.current = null;
      }
      if (selectAllByNameSuggestionHideTimeoutRef.current !== null) {
        window.clearTimeout(selectAllByNameSuggestionHideTimeoutRef.current);
        selectAllByNameSuggestionHideTimeoutRef.current = null;
      }
    };
  }, [
    activeSelectAllSuggestionKey,
    dismissedSelectAllSuggestionKeys,
    periodisationSelectMode,
    renderSelectAllByNameSuggestion,
    selectAllByNameSuggestion,
    selectAllByNameSuggestionKey,
  ]);

  useEffect(() => {
    if (periodisationSelectMode) return;
    setDismissedSelectAllSuggestionKeys([]);
    setActiveSelectAllSuggestionKey(null);
    setShowSelectAllByNameSuggestion(false);
    setRenderSelectAllByNameSuggestion(null);
  }, [periodisationSelectMode]);

  useEffect(() => {
    if (isReadOnlyLinkedPlan) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isTypingTarget =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target?.isContentEditable;
      if (isTypingTarget) return;
      if (
        periodisationDialogOpen ||
        editingScheduleItemId ||
        selectedDateForCreate
      ) {
        return;
      }

      if (event.key === "Escape") {
        if (copyClipboard) return;
        if (
          !periodisationSelectMode &&
          selectedPeriodisationItemIds.length === 0
        ) {
          return;
        }
        event.preventDefault();
        setPeriodisationSelectMode(false);
        setSelectedPeriodisationItemIds([]);
        selectionAnchorIdRef.current = null;
        setPeriodisationBoxSelectRect(null);
        return;
      }

      if (
        (event.key === "Backspace" || event.key === "Delete") &&
        selectedPeriodisationItemIds.length > 0
      ) {
        event.preventDefault();
        removeScheduleItems(selectedPeriodisationItemIds);
        return;
      }

      if (
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "c" &&
        selectedPeriodisationItemIds.length > 0 &&
        !isFixedMode
      ) {
        event.preventDefault();
        beginCopy(selectedPeriodisationItems);
        return;
      }

      if (
        event.key === "Enter" &&
        periodisationSelectMode &&
        selectedPeriodisationItemIds.length > 0
      ) {
        event.preventDefault();
        setPeriodisationDialogOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    beginCopy,
    copyClipboard,
    editingScheduleItemId,
    isFixedMode,
    isReadOnlyLinkedPlan,
    periodisationDialogOpen,
    periodisationSelectMode,
    removeScheduleItems,
    selectedDateForCreate,
    selectedPeriodisationItemIds,
    selectedPeriodisationItems,
  ]);

  useEffect(() => {
    if (!isPeriodisationBoxSelecting || !hasPeriodisationBoxSelectionStarted) {
      return;
    }

    const runAutoScroll = () => {
      const pointer = boxSelectLastClientRef.current;
      if (!pointer || !isPeriodisationBoxSelecting) return;

      const edge = 84;
      const maxStep = 18;
      let deltaY = 0;
      const scrollContainer = boxSelectScrollContainerRef.current;

      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        if (pointer.y > containerRect.bottom - edge) {
          const ratio = (pointer.y - (containerRect.bottom - edge)) / edge;
          deltaY = Math.ceil(ratio * maxStep);
        } else if (pointer.y < containerRect.top + edge) {
          const ratio = (containerRect.top + edge - pointer.y) / edge;
          deltaY = -Math.ceil(ratio * maxStep);
        }
        if (deltaY !== 0) {
          const before = scrollContainer.scrollTop;
          const maxTop =
            scrollContainer.scrollHeight - scrollContainer.clientHeight;
          scrollContainer.scrollTop = Math.min(
            Math.max(0, before + deltaY),
            maxTop,
          );
          if (scrollContainer.scrollTop !== before) {
            updatePeriodisationBoxSelection(pointer.x, pointer.y);
          }
        }
      } else {
        if (pointer.y > window.innerHeight - edge) {
          const ratio = (pointer.y - (window.innerHeight - edge)) / edge;
          deltaY = Math.ceil(ratio * maxStep);
        } else if (pointer.y < edge) {
          const ratio = (edge - pointer.y) / edge;
          deltaY = -Math.ceil(ratio * maxStep);
        }
        if (deltaY !== 0) {
          const before = window.scrollY;
          window.scrollBy({ top: deltaY });
          if (window.scrollY !== before) {
            updatePeriodisationBoxSelection(pointer.x, pointer.y);
          }
        }
      }

      boxSelectAutoScrollRafRef.current =
        window.requestAnimationFrame(runAutoScroll);
    };

    const handleScroll = () => {
      const pointer = boxSelectLastClientRef.current;
      if (!pointer) return;
      updatePeriodisationBoxSelection(pointer.x, pointer.y);
    };
    const scrollContainer = boxSelectScrollContainerRef.current;

    boxSelectAutoScrollRafRef.current =
      window.requestAnimationFrame(runAutoScroll);
    window.addEventListener("scroll", handleScroll, { passive: true });
    scrollContainer?.addEventListener("scroll", handleScroll, {
      passive: true,
    });

    return () => {
      if (boxSelectAutoScrollRafRef.current !== null) {
        window.cancelAnimationFrame(boxSelectAutoScrollRafRef.current);
        boxSelectAutoScrollRafRef.current = null;
      }
      window.removeEventListener("scroll", handleScroll);
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [
    hasPeriodisationBoxSelectionStarted,
    isPeriodisationBoxSelecting,
    updatePeriodisationBoxSelection,
  ]);

  const handleCreateWorkout = () => {
    if (!selectedDateForCreate) return;
    let createdScheduleItemId: string | undefined;
    if (isFixedMode || isTemplateRelativeMode) {
      createdScheduleItemId = onCreateWorkoutForDay(
        scheduleSlotFromDateKey(selectedDateForCreate),
        newWorkoutName,
      );
    } else {
      createdScheduleItemId = onCreateWorkoutForDate(
        selectedDateForCreate,
        newWorkoutName,
      );
    }
    if (createdScheduleItemId) {
      setEditingScheduleItemId(createdScheduleItemId);
    }
    setSelectedDateForCreate(null);
    setNewWorkoutName("");
  };

  const handleImportSelected = (workout: Workout) => {
    if (isFixedMode && selectedFixedDayForSchedule !== null) {
      onImportWorkoutForDay(selectedFixedDayForSchedule, workout);
      setImportOpen(false);
      setSelectedFixedDayForSchedule(null);
      setNewWorkoutName("");
      return;
    }
    if (!selectedDateForCreate) return;
    if (isTemplateRelativeMode) {
      onImportWorkoutForDay(
        scheduleSlotFromDateKey(selectedDateForCreate),
        workout,
      );
    } else if (!isFixedMode) {
      onImportWorkoutForDate(selectedDateForCreate, workout);
    }
    setImportOpen(false);
    setSelectedDateForCreate(null);
    setNewWorkoutName("");
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    if (isReadOnlyLinkedPlan) return;
    setActiveScheduleItemId(String(event.active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (isReadOnlyLinkedPlan) return;
    const { active, over } = event;
    setActiveScheduleItemId(null);
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const movingItem = displayScheduleItems.find(
      (item) => item.id === activeId,
    );
    if (!movingItem) return;

    const isDayDrop = overId.startsWith("day-");
    const overItem = isDayDrop
      ? null
      : displayScheduleItems.find((item) => item.id === overId);
    const targetDate = isDayDrop
      ? overId.replace("day-", "")
      : overItem?.scheduled_date;
    if (!targetDate) return;
    if (isFixedMode && !isFixedDateSchedulable(targetDate)) return;

    const isGroupDrag =
      selectedPeriodisationItemIds.includes(activeId) &&
      selectedPeriodisationItemIds.length > 1;
    if (isGroupDrag) {
      const movingIds = new Set(selectedPeriodisationItemIds);
      const stayingOnTarget = displayScheduleItems
        .filter(
          (item) =>
            item.scheduled_date === targetDate && !movingIds.has(item.id),
        )
        .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
      let insertIndex = stayingOnTarget.length;
      if (!isDayDrop && overItem && !movingIds.has(overItem.id)) {
        const overIndex = stayingOnTarget.findIndex(
          (item) => item.id === overItem.id,
        );
        if (overIndex >= 0) insertIndex = overIndex;
      }
      const relocated = relocateScheduleGroup(
        displayScheduleItems.map((item) => ({
          ...item,
          scheduledDate: item.scheduled_date,
          orderIndex: item.order_index ?? 0,
        })),
        selectedPeriodisationItemIds,
        activeId,
        targetDate,
        insertIndex,
      );
      if (!relocated) return;
      const shiftedOutside =
        isTemplateRelativeMode &&
        relocated.some(
          (item) =>
            movingIds.has(item.id) &&
            differenceInCalendarDays(
              new Date(`${item.scheduledDate}T00:00:00`),
              RELATIVE_BASE_DATE,
            ) < 0,
        );
      const shiftedUnschedulable =
        isFixedMode &&
        relocated.some(
          (item) =>
            movingIds.has(item.id) &&
            !isFixedDateSchedulable(item.scheduledDate),
        );
      if (shiftedOutside || shiftedUnschedulable) return;
      const nextGroupItems = relocated.map((item) => ({
        id: item.id,
        workout_id: item.workout_id,
        scheduled_date: item.scheduledDate,
        order_index: item.orderIndex,
      }));
      commitDisplayScheduleItems(
        nextGroupItems,
        [...new Set(nextGroupItems.map((item) => item.scheduled_date))],
        { preservePeriodisationSelection: true },
      );
      return;
    }

    const sourceDate = movingItem.scheduled_date;
    const sourceItems = displayScheduleItems
      .filter((item) => item.scheduled_date === sourceDate)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
    const withoutMoving = displayScheduleItems.filter(
      (item) => item.id !== activeId,
    );
    const targetItemsWithoutMoving = withoutMoving
      .filter((item) => item.scheduled_date === targetDate)
      .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

    let targetIndex = targetItemsWithoutMoving.length;
    if (!isDayDrop && overItem) {
      const overIndex = targetItemsWithoutMoving.findIndex(
        (item) => item.id === overItem.id,
      );
      targetIndex =
        overIndex >= 0 ? overIndex : targetItemsWithoutMoving.length;
    }

    let nextItems: CalendarScheduleItem[] = withoutMoving.map((item) => ({
      ...item,
    }));

    if (sourceDate === targetDate) {
      const dayWithoutMoving = sourceItems.filter(
        (item) => item.id !== activeId,
      );
      const insertIndex = Math.max(
        0,
        Math.min(targetIndex, dayWithoutMoving.length),
      );
      const reordered = [...dayWithoutMoving];
      reordered.splice(insertIndex, 0, {
        ...movingItem,
        scheduled_date: targetDate,
      });
      nextItems = [
        ...nextItems.filter((item) => item.scheduled_date !== sourceDate),
        ...reordered,
      ];
      commitDisplayScheduleItems(nextItems, [sourceDate], {
        preservePeriodisationSelection: true,
      });
      return;
    }

    const insertIndex = Math.max(
      0,
      Math.min(targetIndex, targetItemsWithoutMoving.length),
    );
    const targetReordered = [...targetItemsWithoutMoving];
    targetReordered.splice(insertIndex, 0, {
      ...movingItem,
      scheduled_date: targetDate,
    });

    nextItems = [
      ...nextItems.filter((item) => item.scheduled_date !== targetDate),
      ...targetReordered,
    ];

    const normalizedBoth = normalizeScheduleForDate(
      normalizeScheduleForDate(nextItems, sourceDate),
      targetDate,
    );
    commitDisplayScheduleItems(normalizedBoth, [sourceDate, targetDate], {
      preservePeriodisationSelection: true,
    });
  };

  const handleExerciseSelectForEditingWorkout = (
    exercise: Tables<"exercises">,
  ) => {
    if (isReadOnlyLinkedPlan) return;
    if (!editingWorkout) return;
    const nextExercise = createWorkoutExercise({
      exercise,
      exOrder: editingWorkout.exerciseCollection.length,
      subOrder: 0,
    });

    onWorkoutUpdated({
      ...editingWorkout,
      exerciseCollection: [
        ...editingWorkout.exerciseCollection,
        [nextExercise],
      ],
    });
    setHasChanges(true);
    window.setTimeout(() => {
      editingWorkoutBottomRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }, 120);
  };

  const handleExerciseCreatedForEditingWorkout = (
    exercise: LibraryExercise,
  ) => {
    handleExerciseSelectForEditingWorkout(exercise);
  };
  const selectedCreateLabel = useMemo(() => {
    if (!selectedDateForCreate) return "";
    if (isTemplateRelativeMode) {
      return `${relativeDayOffsetFromDateKey(selectedDateForCreate) + 1}`;
    }
    return format(new Date(selectedDateForCreate), "EEE, MMM d");
  }, [
    isTemplateRelativeMode,
    relativeDayOffsetFromDateKey,
    selectedDateForCreate,
  ]);
  const templateRelativeWeekRangeLabel = useMemo(() => {
    const startWeek = Math.floor(effectiveRelativePageStartOffset / 7) + 1;
    const weeksInPage = Math.max(1, Math.floor(relativePageSize / 7));
    const endWeek = startWeek + weeksInPage - 1;
    return `Weeks ${startWeek} - ${endWeek}`;
  }, [effectiveRelativePageStartOffset, relativePageSize]);

  const syncCalendarHorizontalScroll = (
    source: HTMLDivElement,
    target: HTMLDivElement | null,
  ) => {
    if (!target || syncingCalendarScrollRef.current) return;
    if (target.scrollLeft === source.scrollLeft) return;
    syncingCalendarScrollRef.current = true;
    target.scrollLeft = source.scrollLeft;
    syncingCalendarScrollRef.current = false;
  };

  return (
    <>
      <div ref={containerRef} className="pb-16">
        <div className="sticky top-0 z-30 bg-background">
        <div className="mb-2 flex min-h-14 flex-wrap items-center gap-2 bg-background/95 py-2 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:gap-3 md:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {isClientDatedMode ? (
              <>
                <Button variant="outline" size="sm" onClick={goToToday}>
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={navigatePrevious}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={navigateNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            ) : isTemplateRelativeMode ? (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={navigatePrevious}
                  disabled={effectiveRelativePageStartOffset === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={navigateNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            ) : null}
            <div className="inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium">
              <CalendarDays className="h-4 w-4" />
              {isFixedMode
                ? `${fixedScheduleViewWeeks === 2 ? "2-Week" : "1-Week"} Pattern`
                : isTemplateRelativeMode
                  ? templateRelativeWeekRangeLabel
                  : format(anchorDate, "MMM yyyy")}
            </div>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-2 md:justify-end">
            {isTemplateLinked ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => onOpenLinkedTemplate?.()}
                disabled={!onOpenLinkedTemplate}
              >
                <Link2 className="h-3.5 w-3.5" />
                Linked template
              </Button>
            ) : null}
            <div
              className={cn(
                "inline-flex rounded-md",
                isFixedMode && "border p-1",
              )}
            >
              {!isFixedMode ? (
                <Button
                  size="sm"
                  variant={periodisationSelectMode ? "default" : "ghost"}
                  onClick={togglePeriodisationSelectMode}
                  disabled={Boolean(copyClipboard) || isReadOnlyLinkedPlan}
                >
                  <TrendingUp className="mr-1 h-4 w-4" />
                  Periodize
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant={fixedScheduleViewWeeks === 1 ? "default" : "ghost"}
                    onClick={() => onFixedScheduleViewWeeksChange(1)}
                  >
                    1 Week
                  </Button>
                  <Button
                    size="sm"
                    variant={fixedScheduleViewWeeks === 2 ? "default" : "ghost"}
                    onClick={() => onFixedScheduleViewWeeksChange(2)}
                  >
                    2 Weeks
                  </Button>
                </>
              )}
            </div>
            <TooltipProvider delayDuration={200}>
              <ToggleGroup
                type="single"
                value={cardsExpanded ? "expanded" : "collapsed"}
                onValueChange={(value) => {
                  if (value !== "expanded" && value !== "collapsed") return;
                  setCardsExpanded(value === "expanded");
                }}
                aria-label="Workout card density"
                className="h-9 gap-0 rounded-md bg-muted p-1"
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <ToggleGroupItem
                        value="collapsed"
                        aria-label="Collapsed"
                        className="h-7 w-8 min-w-0 rounded-sm px-0 hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                      >
                        <Minus className="h-4 w-4" />
                      </ToggleGroupItem>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Collapsed</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">
                      <ToggleGroupItem
                        value="expanded"
                        aria-label="Expanded"
                        className="h-7 w-8 min-w-0 rounded-sm px-0 hover:bg-transparent hover:text-foreground data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm"
                      >
                        <ListChevronsUpDown className="h-4 w-4" />
                      </ToggleGroupItem>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>Expanded</TooltipContent>
                </Tooltip>
              </ToggleGroup>
            </TooltipProvider>
            {onUndo && onRedo ? (
              <EditorUndoControls
                canUndo={canUndo}
                canRedo={canRedo}
                disabled={undoDisabled}
                onUndo={onUndo}
                onRedo={onRedo}
              />
            ) : null}
            {!isTemplateRelativeMode &&
            (!isReadOnlyLinkedPlan || (isClientDatedMode && onDeletePlan)) ? (
              <div className="inline-flex items-center rounded-md border p-1">
                {!isReadOnlyLinkedPlan ? (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 border-0 shadow-none"
                    onClick={onSave}
                    disabled={saveDisabled}
                  >
                    Save
                  </Button>
                ) : null}
                {isClientDatedMode && onDeletePlan ? (
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="outline"
                        className="h-8 w-8 border-0 shadow-none"
                      >
                        <span className="sr-only">Calendar plan actions</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => onDeletePlan()}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove plan
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {!isTemplateRelativeMode ? (
          <div
            ref={weekdayHeaderScrollRef}
            className="overflow-x-auto overflow-y-hidden rounded-t-xl border border-b-0 bg-background [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            onScroll={(event) =>
              syncCalendarHorizontalScroll(
                event.currentTarget,
                calendarBodyScrollRef.current,
              )
            }
          >
            <div className="grid min-w-[1200px] grid-cols-7">
              {weekdayHeaderLabels.map((label, weekdayIndex) => {
                const selectionState = weekdaySelectionState[weekdayIndex];
                return (
                  <button
                    key={`${label}-${weekdayIndex}`}
                    type="button"
                    data-periodisation-weekday-header="true"
                    onClick={() => {
                      if (consumeSelectionClick()) return;
                      if (!periodisationSelectMode || isReadOnlyLinkedPlan)
                        return;
                      toggleWeekdayPeriodisationSelection(weekdayIndex);
                    }}
                    title={
                      periodisationSelectMode
                        ? `Toggle ${label} workouts in current view`
                        : undefined
                    }
                    aria-pressed={
                      periodisationSelectMode
                        ? (selectionState?.allSelected ?? false)
                        : undefined
                    }
                    disabled={
                      !periodisationSelectMode || isReadOnlyLinkedPlan
                    }
                    data-periodisation-weekday-select={
                      periodisationSelectMode
                        ? selectionState?.allSelected
                          ? "full"
                          : selectionState?.partiallySelected
                            ? "partial"
                            : "none"
                        : "off"
                    }
                    className={cn(
                      "border-r border-b bg-muted/30 px-2 py-2 text-center text-xs font-medium text-muted-foreground",
                      periodisationSelectMode &&
                        !isReadOnlyLinkedPlan &&
                        "cursor-pointer hover:bg-muted/50",
                      periodisationSelectMode &&
                        selectionState?.allSelected &&
                        "bg-primary/15 text-primary dark:bg-primary/25 dark:text-blue-100",
                      periodisationSelectMode &&
                        selectionState?.partiallySelected &&
                        "bg-primary/10 text-primary/90 dark:bg-primary/20 dark:text-blue-100",
                    )}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
        </div>

        <div
          ref={calendarBodyScrollRef}
          className={cn(
            "overflow-x-auto overflow-y-hidden border bg-background",
            isTemplateRelativeMode ? "rounded-xl" : "rounded-b-xl border-t-0",
          )}
          onScroll={(event) =>
            syncCalendarHorizontalScroll(
              event.currentTarget,
              weekdayHeaderScrollRef.current,
            )
          }
        >
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div
              ref={calendarGridRef}
              className={cn(
                "relative grid min-w-[1200px] grid-cols-7 select-none",
                (periodisationSelectMode || isPeriodisationBoxSelecting) &&
                  "cursor-crosshair",
              )}
              onPointerDown={handleCalendarGridPointerDown}
            >
              {calendarDays.map((day) => {
                const dateKey = toDateKey(day);
                const dayItems = scheduleByDate.get(dateKey) ?? [];
                const isToday = isSameDay(day, new Date());
                const isFixedPadding =
                  isFixedMode && !isFixedDateSchedulable(dateKey);
                const isOutside =
                  isFixedPadding ||
                  (!isFixedMode &&
                    !isTemplateRelativeMode &&
                    !isSameMonth(day, anchorDate));
                const relativeDayLabel = isTemplateRelativeMode
                  ? relativeDayOffsetFromDateKey(dateKey) + 1
                  : null;

                return (
                  <CalendarDayDropZone
                    key={dateKey}
                    dayKey={dateKey}
                    dataDayKey={dateKey}
                    itemIds={dayItems.map((item) => item.id)}
                    className={cn(
                      "group min-w-0 border-r border-b pt-2 align-top",
                      cardsExpanded ? "min-h-[300px]" : "min-h-[220px]",
                    )}
                    onClick={() => {
                      if (isFixedPadding) return;
                      handleDayCellClick(dateKey);
                    }}
                  >
                    <div className="mb-2 flex items-center justify-between px-2">
                      {isFixedMode ? (
                        <span className="h-5" />
                      ) : isTemplateRelativeMode ? (
                        <button
                          type="button"
                          className="text-xs font-medium text-foreground"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDayCellClick(dateKey);
                          }}
                        >
                          {relativeDayLabel}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={`text-sm font-medium ${
                            isOutside
                              ? "text-muted-foreground/50"
                              : "text-foreground"
                          } ${isToday ? "text-primary" : ""}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleDayCellClick(dateKey);
                          }}
                        >
                          {format(day, "d")}
                        </button>
                      )}
                      {!isReadOnlyLinkedPlan && !isFixedPadding ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-primary/80 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                          title="Add workout"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            handleDayCellClick(dateKey, { create: true });
                          }}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>

                    <ScrollArea
                      className={cardsExpanded ? "h-[260px]" : "h-[185px]"}
                      viewportClassName="[&>div]:!block [&>div]:!w-full [&>div]:!min-w-0"
                    >
                      <SortableContext
                        items={dayItems.map((item) => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="w-full min-w-0 space-y-2 pb-2 pl-2 pr-4 pt-1">
                          {dayItems.map((item) => {
                            const workout = workoutById.get(item.workout_id);
                            if (!workout) return null;
                            return (
                              <SortableCalendarWorkoutCard
                                key={item.id}
                                itemId={item.id}
                                name={workout.name}
                                expanded={cardsExpanded}
                                sections={
                                  cardsExpanded
                                    ? (cardSectionsByWorkoutId.get(workout.id) ??
                                      [])
                                    : []
                                }
                                collapsedSectionIds={
                                  collapsedSectionIdsByItem[item.id] ??
                                  NO_COLLAPSED_SECTIONS
                                }
                                onToggleSection={(sectionId) =>
                                  toggleCardSection(item.id, sectionId)
                                }
                                isCopySource={
                                  copyClipboard?.sourceItemIds.includes(
                                    item.id,
                                  ) ?? false
                                }
                                isCopyMode={Boolean(copyClipboard)}
                                isPeriodisationSelectMode={
                                  periodisationSelectMode
                                }
                                isPeriodisationSelected={selectedPeriodisationItemIds.includes(
                                  item.id,
                                )}
                                isGroupDragGhost={
                                  activeScheduleItemId != null &&
                                  activeScheduleItemId !== item.id &&
                                  selectedPeriodisationItemIds.length > 1 &&
                                  selectedPeriodisationItemIds.includes(
                                    activeScheduleItemId,
                                  ) &&
                                  selectedPeriodisationItemIds.includes(item.id)
                                }
                                removeLabel={
                                  isTemplateRelativeMode
                                    ? "Delete"
                                    : isFixedMode
                                      ? "Unschedule"
                                      : "Remove"
                                }
                                allowCopy={
                                  !isFixedMode && !isReadOnlyLinkedPlan
                                }
                                allowRemove={!isReadOnlyLinkedPlan}
                                interactionsDisabled={isReadOnlyLinkedPlan}
                                onToggleExpanded={() =>
                                  setCardsExpanded(!cardsExpanded)
                                }
                                onOpen={() => {
                                  setEditingScheduleItemId(item.id);
                                }}
                                onCopy={() => beginCopy([item])}
                                onRemove={() => removeScheduleItem(item.id)}
                                onTogglePeriodisationSelect={() =>
                                  togglePeriodisationSelection(item.id)
                                }
                                onRangeSelect={() =>
                                  addScheduleRangeToSelection(item.id)
                                }
                                consumeSelectionClick={consumeSelectionClick}
                              />
                            );
                          })}
                        </div>
                      </SortableContext>
                      <ScrollBar orientation="vertical" className="mr-1" />
                    </ScrollArea>
                  </CalendarDayDropZone>
                );
              })}
              {periodisationBoxSelectRect ? (
                <div
                  className="pointer-events-none absolute z-20 border-2 border-primary/70 bg-primary/15 dark:bg-primary/20"
                  style={{
                    left: periodisationBoxSelectRect.left,
                    top: periodisationBoxSelectRect.top,
                    width: periodisationBoxSelectRect.width,
                    height: periodisationBoxSelectRect.height,
                  }}
                />
              ) : null}
            </div>
            <DragOverlay>
              {!isReadOnlyLinkedPlan && activeScheduleItemId ? (
                <CalendarWorkoutOverlayCard
                  item={
                    displayScheduleItems.find(
                      (item) => item.id === activeScheduleItemId,
                    ) ?? null
                  }
                  workoutById={workoutById}
                  expanded={cardsExpanded}
                  sectionsByWorkoutId={cardSectionsByWorkoutId}
                  collapsedSectionIds={
                    collapsedSectionIdsByItem[activeScheduleItemId] ??
                    NO_COLLAPSED_SECTIONS
                  }
                  onToggleSection={(sectionId) =>
                    toggleCardSection(activeScheduleItemId, sectionId)
                  }
                  stackCount={
                    selectedPeriodisationItemIds.includes(
                      activeScheduleItemId,
                    ) && selectedPeriodisationItemIds.length > 1
                      ? selectedPeriodisationItemIds.length
                      : undefined
                  }
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
        {copyClipboard && copyingLabel ? (
          <div
            className="pointer-events-none fixed bottom-5 z-50 flex -translate-x-1/2 justify-center px-4"
            style={{ left: badgeCenterX ?? "50%" }}
          >
            <div className="pointer-events-auto flex items-center gap-2 rounded-full border bg-popover/95 px-2 py-1.5 shadow-lg backdrop-blur-md animate-in fade-in slide-in-from-bottom-2">
              <span className="max-w-[16rem] truncate px-2 text-sm font-medium">
                Copying {copyingLabel}
              </span>
              <span className="hidden text-xs text-muted-foreground sm:inline">
                Click a day to paste, keeping the same gaps
              </span>
              <Button
                size="sm"
                className="rounded-full"
                onClick={() => setCopyClipboard(null)}
              >
                <Check className="h-3 w-3" />
                Done
              </Button>
            </div>
          </div>
        ) : null}
        {selectedPeriodisationItemIds.length > 0 &&
        !copyClipboard &&
        !periodisationDialogOpen &&
        !editingScheduleItemId ? (
          <div
            className="pointer-events-none fixed bottom-5 z-50 -translate-x-1/2"
            style={{ left: badgeCenterX ?? "50%" }}
          >
            {renderSelectAllByNameSuggestion ? (
              <button
                type="button"
                onClick={selectAllUpcomingWorkoutsByName}
                className={cn(
                  "pointer-events-auto absolute right-full top-1/2 mr-2 inline-flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-full border bg-popover/95 px-3 py-2 text-sm font-medium shadow-lg backdrop-blur-md transition-all duration-300",
                  showSelectAllByNameSuggestion
                    ? "translate-x-0 opacity-100"
                    : "-translate-x-2 opacity-0 pointer-events-none",
                )}
              >
                <MousePointerClick className="h-4 w-4 text-primary" />
                <span>
                  Select all upcoming{" "}
                  {renderSelectAllByNameSuggestion.displayName} workouts
                </span>
              </button>
            ) : null}
            <CalendarBulkActionBar
              selectedCount={selectedPeriodisationItemIds.length}
              onClear={() => {
                setSelectedPeriodisationItemIds([]);
                selectionAnchorIdRef.current = null;
                setPeriodisationBoxSelectRect(null);
              }}
              onPeriodize={
                !isFixedMode && !isReadOnlyLinkedPlan
                  ? () => setPeriodisationDialogOpen(true)
                  : undefined
              }
              onCopy={
                !isFixedMode && !isReadOnlyLinkedPlan
                  ? () => beginCopy(selectedPeriodisationItems)
                  : undefined
              }
              onRemove={
                !isReadOnlyLinkedPlan
                  ? () => removeScheduleItems(selectedPeriodisationItemIds)
                  : undefined
              }
              removeLabel={
                isTemplateRelativeMode
                  ? "Delete"
                  : isFixedMode
                    ? "Unschedule"
                    : "Remove"
              }
            />
          </div>
        ) : null}
      </div>

      <StartWorkoutDialog
        open={selectedDateForCreate !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedDateForCreate(null);
            setImportOpen(false);
          }
        }}
        libraryPicker={{
          title: `Add workout on ${selectedCreateLabel}`,
          description: "Choose a workout from your library",
          emptyTitle: "Add an empty workout",
          emptyDescription: "Build a workout from scratch for this day",
          analyticsName: "client_training_calendar_create_workout_dialog",
          onEmpty: handleCreateWorkout,
          onSelectTemplate: (template) => {
            handleImportSelected(apiWorkoutToEditor(template));
          },
        }}
      />

      <ImportWorkoutDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onWorkoutSelected={handleImportSelected}
      />

      <Dialog
        open={isFixedMode && selectedFixedDayForSchedule !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedFixedDayForSchedule(null);
          }
        }}
        analyticsName="client_training_calendar_schedule_workout_dialog"
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add workout to schedule</DialogTitle>
          </DialogHeader>
          {workouts.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No workouts in this plan yet.
            </div>
          ) : (
            <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Schedule Existing
              </div>
              {workouts.map((workout) => (
                <Button
                  key={`fixed-schedule-${workout.id}`}
                  type="button"
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    if (selectedFixedDayForSchedule === null) return;
                    onAddExistingWorkoutToDay(
                      selectedFixedDayForSchedule,
                      workout,
                    );
                    setSelectedFixedDayForSchedule(null);
                  }}
                >
                  {workout.name}
                </Button>
              ))}
              <Separator className="my-3" />
              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  Or Create / Import
                </div>
                <Input
                  placeholder="Workout name"
                  value={newWorkoutName}
                  onChange={(event) => setNewWorkoutName(event.target.value)}
                />
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    onClick={() => {
                      if (selectedFixedDayForSchedule === null) return;
                      const createdScheduleItemId = onCreateWorkoutForDay(
                        selectedFixedDayForSchedule,
                        newWorkoutName,
                      );
                      if (createdScheduleItemId) {
                        setEditingScheduleItemId(createdScheduleItemId);
                      }
                      setSelectedFixedDayForSchedule(null);
                      setNewWorkoutName("");
                    }}
                  >
                    Create Workout
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setImportOpen(true)}
                  >
                    Import from Library
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={editingWorkout !== null}
        onOpenChange={(open) => {
          if (!open) setEditingScheduleItemId(null);
        }}
        analyticsName="client_training_calendar_edit_workout_dialog"
      >
        <DialogContent
          className={cn(
            "h-[92vh] w-[95vw] overflow-hidden p-0",
            isReadOnlyLinkedPlan ? "max-w-[760px]" : "max-w-[1100px]",
          )}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          {editingWorkout ? (
            <div className="flex h-full min-h-0 flex-col">
              <DialogHeader className="border-b px-6 pt-4 pb-2">
                <div className="flex flex-col gap-1">
                  <DialogTitle>Edit Workout</DialogTitle>
                  {editingScheduleDateLabel && (
                    <p className="text-muted-foreground text-xs">
                      {editingScheduleDateLabel}
                    </p>
                  )}
                  {isTemplateLinked ? (
                    <div className="mt-1 inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
                      <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span>
                        Training plan is linked. Unlink this plan to make
                        individual client changes.
                      </span>
                    </div>
                  ) : null}
                </div>
              </DialogHeader>
              <WorkoutEditorWorkspace
                workout={editingWorkout}
                updateWorkout={(nextWorkout) => {
                  if (isReadOnlyLinkedPlan) return;
                  onWorkoutUpdated(nextWorkout);
                  setHasChanges(true);
                }}
                setHasChanges={setHasChanges}
                className="flex min-h-0 flex-1"
              >
                <div className="relative flex min-h-0 flex-1 flex-col">
                <div
                  className={cn(
                    "min-h-0 flex-1 overflow-y-auto px-6 py-4",
                    aiWorkoutBuilder && !isReadOnlyLinkedPlan && commitWorkout && "pb-28",
                  )}
                >
                  <div className="mx-auto w-full max-w-[640px]">
                    <EditWorkoutView
                      workout={editingWorkout}
                      updateWorkout={(nextWorkout) => {
                        if (isReadOnlyLinkedPlan) return;
                        onWorkoutUpdated(nextWorkout);
                        setHasChanges(true);
                      }}
                      name={{
                        value: editingWorkout.name,
                        valueChange: (value) => {
                          if (isReadOnlyLinkedPlan) return;
                          onWorkoutUpdated({ ...editingWorkout, name: value });
                          setHasChanges(true);
                        },
                      }}
                      description={{
                        value: editingWorkout.description ?? "",
                        valueChange: (value) => {
                          if (isReadOnlyLinkedPlan) return;
                          onWorkoutUpdated({
                            ...editingWorkout,
                            description: value,
                          });
                          setHasChanges(true);
                        },
                      }}
                      hasChanges={hasChanges}
                      setHasChanges={setHasChanges}
                      removeExercise={(exercise) =>
                        isReadOnlyLinkedPlan
                          ? undefined
                          : onRemoveExerciseFromWorkout(
                              editingWorkout.id,
                              exercise,
                            )
                      }
                      deleteWorkout={() => {
                        if (isReadOnlyLinkedPlan) return;
                        onDeleteWorkout(editingWorkout);
                        setEditingScheduleItemId(null);
                      }}
                      readOnly={isReadOnlyLinkedPlan}
                      clientId={clientId}
                    />
                    <div ref={editingWorkoutBottomRef} />
                  </div>
                </div>
                {!isReadOnlyLinkedPlan && commitWorkout && aiWorkoutBuilder ? (
                  <AiWorkoutBar
                    placement="docked"
                    exerciseCollection={editingWorkout.exerciseCollection}
                    commit={(producer) => {
                      commitWorkout(editingWorkout.id, producer);
                    }}
                    onEdited={() => {
                      setTimeout(() => {
                        editingWorkoutBottomRef.current?.scrollIntoView({
                          behavior: "smooth",
                        });
                      }, 200);
                    }}
                  />
                ) : null}
                </div>
                {!isReadOnlyLinkedPlan ? (
                  <ExerciseSelectList
                    exerciseSelected={handleExerciseSelectForEditingWorkout}
                    enableDragToWorkout
                    onExerciseCreated={handleExerciseCreatedForEditingWorkout}
                    className="client-chat-hide-exercise-select hidden h-full min-w-[340px] max-w-[380px] flex-1 rounded-none border-b-0 border-r-0 border-t-0 shadow-none xl:flex"
                  />
                ) : null}
              </WorkoutEditorWorkspace>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog
        open={periodisationDialogOpen}
        onOpenChange={setPeriodisationDialogOpen}
        analyticsName="client_training_calendar_periodisation_dialog"
      >
        <DialogContent className="w-[96vw] max-w-[1700px] min-w-0 max-h-[94vh] overflow-hidden p-0 [&>button]:hidden">
          <div className="relative flex min-h-0 min-w-0 max-h-[94vh] flex-col">
            {periodisationSweepActive ? (
              <div className="pointer-events-none absolute inset-0 z-[45] overflow-hidden">
                <div
                  className={cn(
                    "absolute -inset-y-[14%] w-[120%] rounded-[42%] blur-2xl transition-transform ease-out",
                    periodisationSweepMotion
                      ? "translate-x-[185%]"
                      : "-translate-x-full",
                  )}
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, rgba(139,92,246,0) 0%, rgba(139,92,246,0.28) 28%, rgba(217,70,239,0.32) 52%, rgba(236,72,153,0.2) 68%, rgba(236,72,153,0) 100%), radial-gradient(140px 70px at 22% 48%, rgba(216,180,254,0.45) 0%, rgba(216,180,254,0) 72%), radial-gradient(180px 86px at 45% 55%, rgba(196,181,253,0.4) 0%, rgba(196,181,253,0) 74%), radial-gradient(150px 72px at 70% 46%, rgba(244,114,182,0.3) 0%, rgba(244,114,182,0) 70%)",
                    transitionDuration: `${PERIODISATION_SWEEP_DURATION_MS}ms`,
                  }}
                />
                <div
                  className={cn(
                    "absolute -inset-y-[6%] w-[70%] blur-xl transition-transform ease-out mix-blend-screen",
                    periodisationSweepMotion
                      ? "translate-x-[230%]"
                      : "-translate-x-[130%]",
                  )}
                  style={{
                    backgroundImage:
                      "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(233,213,255,0.34) 45%, rgba(255,255,255,0) 100%)",
                    transitionDuration: `${Math.round(PERIODISATION_SWEEP_DURATION_MS * 0.86)}ms`,
                  }}
                />
              </div>
            ) : null}
            <ScrollArea
              className="min-h-0 min-w-0 w-full h-auto max-h-[82vh]"
            >
              <div className="inline-block min-w-max align-top">
                <PeriodisationMatrix
                  selectedItems={
                    selectedPeriodisationItems as PeriodisationScheduleItem[]
                  }
                  workoutById={workoutById}
                  onChangeExercise={updateWorkoutExerciseFromPeriodisation}
                  onAutoPeriodiseApply={
                    updateWorkoutExercisesFromPeriodisationBatch
                  }
                  onAutoSweepStateChange={setPeriodisationSweepActive}
                  onAutoPeriodiseGenerate={generateAutoPeriodisationFromEdge}
                  showAutoPeriodise={Boolean(isAiAutoPeriodiseEnabled)}
                />
              </div>
              <ScrollBar orientation="horizontal" />
              <ScrollBar orientation="vertical" />
            </ScrollArea>
            <div className="w-full shrink-0 border-t bg-background px-6 py-3">
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setPeriodisationDialogOpen(false);
                    setPeriodisationBoxSelectRect(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}


type CalendarDayDropZoneProps = {
  dayKey: string;
  dataDayKey?: string;
  itemIds: string[];
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
};

const CalendarDayDropZone = ({
  dayKey,
  dataDayKey,
  itemIds,
  className,
  onClick,
  children,
}: CalendarDayDropZoneProps) => {
  const droppableId = `day-${dayKey}`;
  const { setNodeRef } = useDroppable({ id: droppableId });
  const { over, active } = useDndContext();
  const overId = over ? String(over.id) : "";
  const isOver = overId === droppableId || itemIds.includes(overId);
  const isDragging = Boolean(active);

  return (
    <div
      ref={setNodeRef}
      data-calendar-day-key={dataDayKey}
      className={cn(
        className,
        isOver && "bg-primary/5",
        isDragging && "transition",
      )}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const CALENDAR_CARD_DENSITY_KEY = "trainrr.calendar.cardDensity";

const SECTION_ACCENT_COLORS = [
  "bg-cyan-400",
  "bg-blue-500",
  "bg-amber-300",
  "bg-violet-400",
  "bg-emerald-400",
  "bg-rose-400",
];

type CalendarCardExercise = {
  letter: string;
  name: string;
};

type CalendarCardSection = {
  id: string;
  label: string | null;
  exercises: CalendarCardExercise[];
};

const readCardsExpanded = () => {
  try {
    return window.localStorage.getItem(CALENDAR_CARD_DENSITY_KEY) !== "collapsed";
  } catch {
    return true;
  }
};

const exerciseLetter = (index: number) => {
  let n = index;
  let label = "";
  do {
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return label;
};

const groupExerciseLabels = (
  group: { name: string }[],
  letterIndex: number,
): CalendarCardExercise[] => {
  const letter = exerciseLetter(letterIndex);
  return group.map((exercise, position) => ({
    letter: group.length > 1 ? `${letter}${position + 1}` : letter,
    name: exercise.name,
  }));
};

const NO_COLLAPSED_SECTIONS: readonly string[] = [];

const stableExerciseAnchor = (
  group: { sortableId?: string; workout_exercise_id?: string | null }[],
  fallback: string,
) => group[0]?.sortableId || group[0]?.workout_exercise_id || fallback;

const withStableDefaultSection = (workout: Workout): Workout => {
  if (workout.sections?.length) return ensureWorkoutSections(workout);
  const ensured = ensureWorkoutSections(workout);
  const stableId = `${workout.id}-default`;
  const generatedId = ensured.sections[0]?.id;
  if (!generatedId || generatedId === stableId) return ensured;
  return {
    ...ensured,
    sections: ensured.sections.map((section, index) =>
      index === 0 ? { ...section, id: stableId } : section,
    ),
    exerciseCollection: ensured.exerciseCollection.map((group) =>
      group.map((exercise) => ({
        ...exercise,
        section_id:
          !exercise.section_id || exercise.section_id === generatedId
            ? stableId
            : exercise.section_id,
      })),
    ),
  };
};

const calendarCardSections = (workout: Workout): CalendarCardSection[] => {
  const ensured = withStableDefaultSection(workout);
  let letterIndex = 0;
  const blocks: CalendarCardSection[] = [];

  ensured.sections.forEach((section) => {
    const groups = groupsForSection(ensured, section.id);
    if (groups.length === 0) return;

    if (!isLooseSection(section)) {
      blocks.push({
        id: section.id,
        label: sessionSectionLabel(section),
        exercises: groups.flatMap(({ group }) => {
          const labeled = groupExerciseLabels(group, letterIndex);
          letterIndex += 1;
          return labeled;
        }),
      });
      return;
    }

    let looseExercises: CalendarCardExercise[] = [];
    let looseAnchor = "";
    const flushLoose = () => {
      if (looseExercises.length === 0) return;
      blocks.push({
        id: `${section.id}-loose-${looseAnchor}`,
        label: null,
        exercises: looseExercises,
      });
      looseExercises = [];
      looseAnchor = "";
    };

    groups.forEach(({ group }, groupIndex) => {
      const labeled = groupExerciseLabels(group, letterIndex);
      letterIndex += 1;
      const anchor = stableExerciseAnchor(group, String(groupIndex));
      if (group.length > 1) {
        flushLoose();
        blocks.push({
          id: `${section.id}-superset-${anchor}`,
          label: "Superset",
          exercises: labeled,
        });
        return;
      }
      if (looseExercises.length === 0) looseAnchor = anchor;
      looseExercises.push(...labeled);
    });
    flushLoose();
  });

  return blocks.filter((block) => block.exercises.length > 0);
};

const sectionClickSelectsCard = (
  event: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
  selectionActive: boolean,
) =>
  selectionActive || event.shiftKey || event.metaKey || event.ctrlKey;

const CalendarWorkoutExerciseList = ({
  sections,
  collapsedSectionIds,
  onToggleSection,
  selectionActive = false,
}: {
  sections: CalendarCardSection[];
  collapsedSectionIds: readonly string[];
  onToggleSection: (sectionId: string) => void;
  selectionActive?: boolean;
}) => {
  if (sections.length === 0) return null;
  return (
    <div className="mt-1.5 space-y-1.5">
      {sections.map((section, index) => {
        const collapsed = collapsedSectionIds.includes(section.id);
        return (
          <div key={section.id} className="flex min-w-0 gap-1.5">
            {section.label ? (
              <div
                className={cn(
                  "w-0.5 shrink-0 self-stretch rounded-full",
                  SECTION_ACCENT_COLORS[index % SECTION_ACCENT_COLORS.length],
                )}
              />
            ) : null}
            <div className="min-w-0 flex-1">
              {section.label ? (
                <button
                  type="button"
                  className="flex min-w-0 items-center gap-0.5 text-left text-[10px] leading-4 text-muted-foreground hover:text-foreground"
                  aria-expanded={!collapsed}
                  title={collapsed ? `Show ${section.label}` : `Hide ${section.label}`}
                  onPointerDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    if (sectionClickSelectsCard(event, selectionActive)) return;
                    event.stopPropagation();
                    onToggleSection(section.id);
                  }}
                >
                  <ChevronDown
                    className={cn(
                      "h-3 w-3 shrink-0 transition-transform",
                      collapsed && "-rotate-90",
                    )}
                  />
                  <span className="min-w-0 break-words">{section.label}</span>
                </button>
              ) : null}
              {collapsed
                ? null
                : section.exercises.map((exercise) => (
                    <div
                      key={`${section.id}-${exercise.letter}`}
                      className="flex min-w-0 items-baseline gap-1.5 text-[11px] leading-4"
                    >
                      <span className="shrink-0 font-medium text-muted-foreground">
                        {exercise.letter}
                      </span>
                      <span className="min-w-0 break-words" title={exercise.name}>
                        {exercise.name}
                      </span>
                    </div>
                  ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

type SortableCalendarWorkoutCardProps = {
  itemId: string;
  name: string;
  expanded: boolean;
  sections: CalendarCardSection[];
  collapsedSectionIds: readonly string[];
  onToggleSection: (sectionId: string) => void;
  isCopySource: boolean;
  isCopyMode: boolean;
  isPeriodisationSelectMode: boolean;
  isPeriodisationSelected: boolean;
  isGroupDragGhost?: boolean;
  removeLabel?: string;
  allowCopy?: boolean;
  allowRemove?: boolean;
  interactionsDisabled?: boolean;
  onOpen: () => void;
  onToggleExpanded: () => void;
  onCopy: () => void;
  onRemove: () => void;
  onTogglePeriodisationSelect: () => void;
  onRangeSelect: () => void;
  consumeSelectionClick: () => boolean;
};

const SortableCalendarWorkoutCard = ({
  itemId,
  name,
  expanded,
  sections,
  collapsedSectionIds,
  onToggleSection,
  isCopySource,
  isCopyMode,
  isPeriodisationSelectMode,
  isPeriodisationSelected,
  isGroupDragGhost = false,
  removeLabel = "Remove",
  allowCopy = true,
  allowRemove = true,
  interactionsDisabled = false,
  onOpen,
  onToggleExpanded,
  onCopy,
  onRemove,
  onTogglePeriodisationSelect,
  onRangeSelect,
  consumeSelectionClick,
}: SortableCalendarWorkoutCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: itemId,
    disabled: interactionsDisabled || isPeriodisationSelectMode,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger asChild>
        <div
          ref={setNodeRef}
          data-periodisation-item-id={itemId}
          style={style}
          onClick={(event) => {
            event.stopPropagation();
            if (consumeSelectionClick()) return;
            if (event.shiftKey) {
              event.preventDefault();
              if (!interactionsDisabled) onRangeSelect();
              return;
            }
            if (event.metaKey || event.ctrlKey) {
              event.preventDefault();
              if (!interactionsDisabled) onTogglePeriodisationSelect();
              return;
            }
            if (isPeriodisationSelectMode || isPeriodisationSelected) {
              onTogglePeriodisationSelect();
              return;
            }
            if (isCopyMode) return;
            onOpen();
          }}
          className={cn(
            "group relative w-full min-w-0 rounded-md border px-2",
            expanded ? "py-1" : "py-2.5",
            !interactionsDisabled && "cursor-grab active:cursor-grabbing",
            isPeriodisationSelected
              ? "border-primary bg-primary/10 ring-2 ring-primary/35 dark:bg-primary/20"
              : isCopySource
                ? "border-primary bg-primary/10 ring-2 ring-primary/30"
                : "bg-card",
            isPeriodisationSelectMode && "cursor-pointer active:cursor-pointer",
            isDragging && "opacity-70",
            isGroupDragGhost && "opacity-40",
          )}
        >
          <div
            className="min-w-0 pr-6"
            {...attributes}
            {...listeners}
            onPointerDown={(event) => {
              if (event.shiftKey || event.metaKey || event.ctrlKey) {
                event.stopPropagation();
                return;
              }
              listeners?.onPointerDown?.(event);
            }}
          >
            <div
              className={cn(
                "text-xs font-medium",
                expanded ? "break-words leading-snug" : "truncate",
              )}
            >
              {name}
            </div>
          </div>
          {expanded ? (
            <div
              className="min-w-0"
              {...listeners}
              onPointerDown={(event) => {
                if (event.shiftKey || event.metaKey || event.ctrlKey) {
                  event.stopPropagation();
                  return;
                }
                listeners?.onPointerDown?.(event);
              }}
            >
              <CalendarWorkoutExerciseList
                sections={sections}
                collapsedSectionIds={collapsedSectionIds}
                onToggleSection={onToggleSection}
                selectionActive={isPeriodisationSelectMode}
              />
            </div>
          ) : null}
          <div
            className={cn(
              "group/actions absolute right-0.5 z-10 h-5 w-5",
              expanded ? "top-0.5" : "top-1/2 -translate-y-1/2",
            )}
          >
            {isPeriodisationSelected ? (
              <span className="pointer-events-none absolute inset-0 inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity group-hover/actions:opacity-0 group-has-[[data-state=open]]/actions:opacity-0">
                <Check className="h-3 w-3" />
              </span>
            ) : null}
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-5 w-5 transition-opacity focus-visible:opacity-100 data-[state=open]:opacity-100",
                      isPeriodisationSelected
                        ? "opacity-0 hover:opacity-100"
                        : "opacity-0 group-hover:opacity-100",
                    )}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen();
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" />
                    Edit workout
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleExpanded();
                    }}
                  >
                    {expanded ? (
                      <ChevronsDownUp className="mr-2 h-4 w-4" />
                    ) : (
                      <ChevronsUpDown className="mr-2 h-4 w-4" />
                    )}
                    {expanded ? "Collapse all cards" : "Expand all cards"}
                  </DropdownMenuItem>
                  {!interactionsDisabled ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onTogglePeriodisationSelect();
                      }}
                    >
                      {isPeriodisationSelected ? (
                        <SquareX className="mr-2 h-4 w-4" />
                      ) : (
                        <CheckSquare className="mr-2 h-4 w-4" />
                      )}
                      {isPeriodisationSelected ? "Unselect" : "Select"}
                    </DropdownMenuItem>
                  ) : null}
                  {allowCopy ? (
                    <DropdownMenuItem
                      onClick={(event) => {
                        event.stopPropagation();
                        onCopy();
                      }}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </DropdownMenuItem>
                  ) : null}
                  {allowCopy && allowRemove ? <DropdownMenuSeparator /> : null}
                  {allowRemove ? (
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      {removeLabel}
                    </DropdownMenuItem>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
          </div>
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onClick={(event) => {
            event.stopPropagation();
            onOpen();
          }}
        >
          <Pencil className="mr-2 h-4 w-4" />
          Edit workout
        </ContextMenuItem>
        <ContextMenuItem
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpanded();
          }}
        >
          {expanded ? (
            <ChevronsDownUp className="mr-2 h-4 w-4" />
          ) : (
            <ChevronsUpDown className="mr-2 h-4 w-4" />
          )}
          {expanded ? "Collapse all cards" : "Expand all cards"}
        </ContextMenuItem>
        {!interactionsDisabled ? (
          <ContextMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onTogglePeriodisationSelect();
            }}
          >
            {isPeriodisationSelected ? (
              <SquareX className="mr-2 h-4 w-4" />
            ) : (
              <CheckSquare className="mr-2 h-4 w-4" />
            )}
            {isPeriodisationSelected ? "Unselect" : "Select"}
          </ContextMenuItem>
        ) : null}
        {allowCopy ? (
          <ContextMenuItem
            onClick={(event) => {
              event.stopPropagation();
              onCopy();
            }}
          >
            <Copy className="mr-2 h-4 w-4" />
            Copy
          </ContextMenuItem>
        ) : null}
        {allowCopy && allowRemove ? (
          <ContextMenuSeparator />
        ) : null}
        {allowRemove ? (
          <ContextMenuItem
            variant="destructive"
            onClick={(event) => {
              event.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {removeLabel}
          </ContextMenuItem>
        ) : null}
      </ContextMenuContent>
    </ContextMenu>
  );
};

type CalendarWorkoutOverlayCardProps = {
  item: CalendarScheduleItem | null;
  workoutById: Map<string, Workout>;
  expanded?: boolean;
  sectionsByWorkoutId: Map<string, CalendarCardSection[]>;
  collapsedSectionIds: readonly string[];
  onToggleSection: (sectionId: string) => void;
  stackCount?: number;
};

const CalendarWorkoutOverlayCard = ({
  item,
  workoutById,
  expanded = false,
  sectionsByWorkoutId,
  collapsedSectionIds,
  onToggleSection,
  stackCount,
}: CalendarWorkoutOverlayCardProps) => {
  if (!item) return null;
  const workout = workoutById.get(item.workout_id);
  const name = workout?.name ?? "Workout";
  const visibleStack = Math.min(Math.max(stackCount ?? 1, 1), 3);
  const isStack = visibleStack > 1;
  const backingOffsets = Array.from(
    { length: visibleStack - 1 },
    (_, index) => visibleStack - 1 - index,
  );
  const sections =
    expanded && !isStack && workout
      ? (sectionsByWorkoutId.get(workout.id) ?? [])
      : [];

  return (
    <div className={cn("relative cursor-grabbing", expanded ? "w-48" : "w-40")}>
      {backingOffsets.map((offset) => (
        <div
          key={offset}
          className="absolute inset-0 rounded-lg border bg-background shadow-sm"
          style={{
            transform: `translate(${offset * 4}px, ${offset * 4}px)`,
          }}
        />
      ))}
      <div
        className={cn(
          "relative rounded-lg border bg-card px-2.5 shadow-lg",
          expanded || isStack ? "py-2" : "py-2.5",
        )}
      >
        {isStack ? (
          <span className="absolute -right-2 -top-2 z-10 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold leading-none text-primary-foreground">
            {stackCount}
          </span>
        ) : null}
        <div
          className={cn(
            "text-xs font-medium",
            expanded && !isStack ? "line-clamp-2 leading-snug" : "truncate",
          )}
        >
          {name}
        </div>
        {isStack ? (
          <div className="text-[11px] text-muted-foreground">
            {stackCount} workouts
          </div>
        ) : expanded ? (
          <CalendarWorkoutExerciseList
            sections={sections}
            collapsedSectionIds={collapsedSectionIds}
            onToggleSection={onToggleSection}
          />
        ) : null}
      </div>
    </div>
  );
};
